<!-- src/pages/app/public/ticketing/TicketWidget.vue -->
<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTicketingWidgetApi } from '@/composables/api/ticketing/useTicketingWidgetApi'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { loadStripe, type Stripe, type StripeElements } from '@stripe/stripe-js'
import LoadingIcon from "@/components/Base/LoadingIcon";
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'

dayjs.extend(utc)
dayjs.extend(timezone)

// --- Debounced rehydrate ---
let rehydrateTimer: number | undefined


/* =======================
   Types (only what we use)
   ======================= */
type Ticket = {
  id: number
  ticket_group_id: number
  name: string
  description?: string | null
  price_cents: number
  price_cents_ex?: number | null
  currency: string
  min_quantity?: number | null
  max_quantity?: number | null
  quantity_available?: number | null
  sold_out: boolean
  on_sale?: boolean
  sales_ended?: boolean
  sellable?: boolean
  sort_order?: number | null
  sales_window?: { start?: string | null; end?: string | null }
  show_sales_end_date?: boolean
  is_hidden?: boolean
  was_revealed?: boolean
  effective_visible?: boolean
  effective_sellable?: boolean  
}

type SessionPreview = Record<
  number,
  {
    unit_effective_cents_inc: number | null
    unit_compare_at_cents_inc: number | null
  }
>

type Group = { 
  id: number
  name?: string
  label?: string
  tickets: Ticket[]
  sellable_ticket_count: number
  image?: { id: number; url: string } | null
  is_full_span?: boolean
  display?: any
}
type EventPricing = {
  tax_rate_percent?: number
  processing_fee_rate?: number
  processing_fee_label?: string
  tax_name?: string
  booking_fee_cents_ex?: number | null
  booking_fee_label?: string
}

type EventDisplay = {
  date_range?: string
  schedule?: string
  bottom_time?: string
  ics_url?: string
  google_url?: string
}

type EventAddress = {
  street_1?: string | null
  locality?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  formatted?: string | null
  google_maps_link?: string | null
}

type EventMedia = { url: string | null; filename?: string | null; mime?: string | null; original?: string | null }

type EventPayload = {
  id: string | number
  slug: string
  name: string
  timezone?: string | null
  starts_at?: string | null
  ends_at?: string | null
  currency?: string | null
  images?: EventMedia[]
  email_header_image?: EventMedia | null
  landing_page_image?: EventMedia | null
  popup_image?: EventMedia | null
  landing_page_video?: EventMedia | null
  address?: any
  display?: any
  description?: string | null
  faqs?: any[]
  map?: any
  pricing?: EventPricing
  groups?: Group[]
}

type SessionDiscount = {
  code: string;
  type: string;
  value: number;
  scope: "all" | "subset";
  applied_ticket_ids?: number[];
  eligible_ticket_ids?: number[];
  message?: string;
  max_uses_per_order?: number | null;
  reveals_hidden_tickets?: boolean | null;  
};

// Extend SessionItem with the fields you read in the template (optional but recommended)
type SessionItem = {
  id: number
  ticket_id: number
  ticket_name: string
  quantity: number

  // pricing (EX + INC)
  unit_price_cents_ex: number
  line_total_cents_inc: number

  // compare-at & effective (INC)
  unit_compare_at_cents_inc?: number | null
  unit_effective_cents_inc?: number | null

  // discount info (EX)
  line_discount_cents_ex?: number | null
  was_discounted?: boolean

  // group stuff
  group_label?: string
  group?: { is_full_span?: boolean }
  group_is_full_span?: boolean
}

type SessionPricing = {
  tax_rate_percent?: number
  processing_fee_rate?: number
  processing_fee_label?: string
  tax_name?: string
}
type SessionCaps = {
  type?: 'Reveal' | 'percent' | 'amount' | string
  remaining_by_ticket?: Record<number, number>
  discounted_units_remaining?: number
}
type SessionNotice = { code: string; message: string; ticket_id?: number }

// Keep your SessionTotals / SessionPricing as-is, then update SessionResource like this:
type SessionResource = {
  id: string
  currency: string
  items: SessionItem[]
  totals?: SessionTotals
  pricing?: SessionPricing
  tax_rate_percent?: number
  processing_fee_rate?: number
  accepted_terms?: boolean
  mailing_list?: boolean
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  discount?: SessionDiscount | null
  // NEW: add previews so session.previews[...] is valid
  previews?: SessionPreview
  caps?: SessionCaps
  notices?: SessionNotice[]  
}

/* =======================
   Route & API
   ======================= */
const route = useRoute()
const router = useRouter()
const slug = route.params.slug as string

const {
  fetchEventBySlug,
  createSessionForEvent,
  addSessionItem,
  updateSessionItemQty,
  applySessionDiscount,
  removeSessionDiscount,
  saveSessionConsents,
  createPaymentIntentForSession,
  confirmSessionPayment,
  fetchSessionById,
  validateSessionBeforeCheckout,
  releaseSessionHolds,
  extendSessionHold,
  syncSessionItems,
} = useTicketingWidgetApi()

/* =======================
   State
   ======================= */

const isBooting = ref(true)
const event = ref<EventPayload | null>(null)
const session = ref<SessionResource | null>(null)

const safeTotals = computed<SessionTotals>(() => {
  // When cart is dirty (local mode), use client-computed totals
  if (isDirty.value && clientTotals.value) {
    return {
      subtotal_cents_ex:              0,
      discount_cents_ex:              0,
      discount_cents_inc:             clientTotals.value.discount_cents_inc,
      fees_cents_ex:                  0,
      tax_cents:                      0,
      total_cents_inc:                clientTotals.value.total_cents_inc,
      subtotal_cents_inc:             clientTotals.value.subtotal_cents_inc,
      undiscounted_total_cents_inc:   clientTotals.value.undiscounted_total_cents_inc,
      undiscounted_subtotal_cents_inc: clientTotals.value.undiscounted_subtotal_cents_inc,
      undiscounted_fees_cents_inc:    0,
      booking_fee_cents_inc:          clientTotals.value.booking_fee_cents_inc,
      tax_name:                       session.value?.totals?.tax_name
                                       ?? event.value?.pricing?.tax_name
                                       ?? null,
      processing_fee_label:           session.value?.totals?.processing_fee_label
                                       ?? event.value?.pricing?.processing_fee_label
                                       ?? null,
    }
  }

  // Server-driven mode: use session totals
  return {
    subtotal_cents_ex:              0,
    discount_cents_ex:              0,
    discount_cents_inc:             0,
    fees_cents_ex:                  0,
    tax_cents:                      0,
    total_cents_inc:                0,
    subtotal_cents_inc:             0,
    undiscounted_total_cents_inc:   0,
    undiscounted_subtotal_cents_inc: 0,
    undiscounted_fees_cents_inc:    0,
    tax_name:                       session.value?.totals?.tax_name
                                     ?? event.value?.pricing?.tax_name
                                     ?? null,
    processing_fee_label:           session.value?.totals?.processing_fee_label
                                     ?? event.value?.pricing?.processing_fee_label
                                     ?? null,
    ...(session.value?.totals ?? {}),
  }
})

// What we *display* for subtotal: prefer original (pre-discount) if present
// assuming you already have:
// const safeTotals = computed(() => session.value?.totals ?? {})


const crossedCompareAtTotalInc = computed(() => {
  // Only show a crossed-out total if there actually *is* a discount
  if (!safeTotals.value.discount_cents_inc) return null
  return safeTotals.value.undiscounted_total_cents_inc || null
})

// --- Debounced rehydrate (you already have) ---
const debounceRehydrate = (reason = 'unknown') => {
  clearTimeout(rehydrateTimer);
  rehydrateTimer = window.setTimeout(() => rehydrate(reason), 120);
};

// ---- Rehydrate guards & helpers (add once) ----
const isMobile = () => window.innerWidth <= 1023

let rehydrateNeeded = false         // set when tab/page goes to background
let lastRehydrateAt = 0
const REHYDRATE_MIN_GAP_MS = 1200   // throttle rehydrate calls
let mountingStripe = false          // prevent concurrent Stripe mounts

function markBackgrounded(reason = 'hidden') {
  rehydrateNeeded = true
}

function shouldRehydrateNow(why: string) {
  // allow only lifecycle returns (plus 'initial' on first mount)
  const allowed = ['initial','focus-return','visibility-return','pageshow-return']
  if (!allowed.includes(why)) return false

  if (why !== 'initial' && !rehydrateNeeded) return false

  const now = Date.now()
  if (now - lastRehydrateAt < REHYDRATE_MIN_GAP_MS) return false

  lastRehydrateAt = now
  return true
}

// --- Stable handler refs (for add/removeEventListener symmetry) ---
const onResize = () => debounceRehydrate('resize');
const onOrientation = () => debounceRehydrate('orientationchange');
const onVVResize = () => debounceRehydrate('vv-resize');
const onVVScroll = () => debounceRehydrate('vv-scroll');
const onVis = () => {
  if (document.visibilityState === 'visible') debounceRehydrate('visibilitychange');
};
// 🔧 Missing before: define onFocus so the listener has a real function
const onFocus = () => debounceRehydrate('focus');
// If you want strict typing, keep any to avoid TS complaining about PageTransitionEvent
const onPageShow = (_e: any) => debounceRehydrate('pageshow');


// --- Mount / unmount wiring (your lines ~200) ---
onMounted(() => {
  // initial render may need one rehydrate
  rehydrate('initial')

  // Mark backgrounded when leaving
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') markBackgrounded('visibilitychange')
    if (document.visibilityState === 'visible') {
      // only rehydrate if we truly came back
      if (rehydrateNeeded) rehydrate('visibility-return')
    }
  })

  window.addEventListener('pagehide', () => markBackgrounded('pagehide'))

  window.addEventListener('pageshow', (e: PageTransitionEvent) => {
    if (e.persisted || rehydrateNeeded) rehydrate('pageshow-return')
  })

  window.addEventListener('focus', () => {
    if (rehydrateNeeded) rehydrate('focus-return')
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', () => {})
  window.removeEventListener('orientationchange', () => {})
  window.visualViewport?.removeEventListener('resize', () => {})
  window.visualViewport?.removeEventListener('scroll', () => {})
  document.removeEventListener('visibilitychange', () => {})
  window.removeEventListener('pagehide', () => {})
  window.removeEventListener('pageshow', () => {})
  window.removeEventListener('focus', () => {})
})

// 🔧 helpers (near ticketToGroup/ticketMap)
function isFullSpanGroupByTicket(ticketId: number): boolean {
  const gid = ticketToGroup[ticketId]
  const grp: any = (event.value?.groups || []).find(g => g.id === gid)
  return !!(grp?.is_full_span || grp?.full_span)
}

// OPTIONAL: annotate session items so the summary always has the flag
function tagFullSpanOnSessionItems(s: SessionResource | null) {
  if (!s?.items) return
  s.items = s.items.map(i => ({
    ...i,
    group_is_full_span:
      (i as any).group_is_full_span ??
      (i as any).group?.is_full_span ??
      isFullSpanGroupByTicket(i.ticket_id),
  }))
}

// true only when a percent/amount code makes the NEXT unit cheaper for this ticket
function hasDiscountAppliedBadge(t: Ticket): boolean {
  if (!isPriceDiscount()) return false
  if (!isTicketEligibleForPriceDiscount(t.id)) return false
  // respect caps/previews for the *next* unit
  return nextUnitIsDiscounted(t.id)
}

// text for the badge (kept simple per your request)
function discountBadgeText(): string {
  return 'Discount Applied'
}

const notices = ref<SessionNotice[]>([])

function dismissNotice(idx: number) {
  notices.value = notices.value.filter((_, i) => i !== idx)
}

function clearAllNotices() {
  notices.value = []
}

function applySessionWithNotices(updated: SessionResource) {
  applySession(updated) // ← call the base applySession, not itself
  notices.value = Array.isArray(updated?.notices) ? updated.notices : []
}

function remainingForTicket(tid: number): number | null {
  const caps = session.value?.caps
  if (!caps) return null
  if ((caps.type || '').toLowerCase() !== 'reveal') return null
  return caps.remaining_by_ticket?.[tid] ?? null
}

function isSoldOut(t: Ticket): boolean {
  const typ = (session.value?.discount?.type || '').toLowerCase()
  if (typ !== 'reveal') return false
  const remain = remainingForTicket(t.id)
  return remain === 0
}

// helpers (near your other computed/utility funcs)
function isDiscountContextForTicket(t: Ticket): boolean {
  const s = session.value
  if (!s || !s.discount) return false

  const d = s.discount
  const type = String(d.type || '').toLowerCase()

  // Only style amount/percent discounts, never reveal-only
  if (type !== 'amount' && type !== 'percent') {
    return false
  }

  if ((t.min_quantity ?? 1) > 1) {
    return false
  }  

  // Don’t show discount styling on tickets that aren’t sellable anymore
  if (t.sales_ended || t.sold_out || t.sellable === false) {
    return false
  }

  // Use the per-ticket pricing preview from the session
  const previews: Record<number, {
    unit_effective_cents_inc: number
    unit_compare_at_cents_inc: number | null
  }> = (s as any).previews || {}

  const preview = previews[t.id]
  if (!preview) return false

  const effective  = Number(preview.unit_effective_cents_inc ?? 0)
  const compareAt  = preview.unit_compare_at_cents_inc

  // “Real” discount only if compare-at is higher than effective
  //   - Early Bird (id 12): 1290 vs 2290  => true  => green border
  //   - Free GA Friday (id 1): 0 vs null => false => no border
  if (typeof compareAt === 'number' && compareAt > effective) {
    return true
  }

  return false
}

const hasEndingBadge = (t: Ticket) => {
  // use your existing badge logic; this mirrors "Sales end on …"
  // if you already compute this, reuse it
  return !!t.sales_end_at && dayjs().isBefore(dayjs(t.sales_end_at))
}

const previewFor = (ticketId: number) => {
  const p = (session.value?.previews || {})[String(ticketId)]
  return p ? { inc: p.unit_effective_cents_inc, compareAt: p.unit_compare_at_cents_inc } : null
}

function canIncrement(t: Ticket): boolean {
  // 1) block while pending
  if (pendingByTicket.value[t.id]) return false

  // 2) must be sellable by your effective rules
  if (!isEffectivelySellable(t)) return false

  // 3) hard stock cap based on what's already in the cart
  if (typeof t.quantity_available === 'number') {
    const inCart = qtyByTicket.value[t.id] ?? 0
    const remaining = t.quantity_available - inCart
    if (remaining <= 0) return false
  }

  // 4) per-ticket per-order cap
  if (isPerTicketOrderLimitReached(t)) return false

  // 5) reveal-specific pool + order-level cap
  const typ = (session.value?.discount?.type || '').toLowerCase()
  if (typ === 'reveal' && (isUnlocked(t) || t.was_revealed)) {
    const remain = remainingForTicket(t.id)
    if (remain === 0) return false
    if (revealOrderLimitReachedFor(t)) return false
  }

  return true
}

function ticketNotice(tid: number): string | null {
  // Only return notices specifically for this ticket (not global notices)
  const n = notices.value.find(n => n.ticket_id === tid)
  return n?.message || null
}

const globalNotices = computed(() =>
  (notices.value || []).filter(n => n.ticket_id == null)
);

const ticketToGroup = reactive<Record<number, number>>({})
const ticketMap = reactive<Record<number, Ticket>>({})

const currentGroupId = ref<number | null>(null)
const qtyByTicket = ref<Record<number, number>>({})
const pendingByTicket = ref<Record<number, boolean>>({})

// Local cart state (used when no voucher is applied)
const isDirty = ref(false)
type ClientTotals = {
  subtotal_cents_inc: number
  discount_cents_inc: number
  booking_fee_cents_inc: number
  total_cents_inc: number
  undiscounted_subtotal_cents_inc: number
  undiscounted_total_cents_inc: number
}
const clientTotals = ref<ClientTotals | null>(null)

const code = ref('')
const applyingCode = ref(false)
// Hold UI while we refetch event after a reveal
const isRefreshingAfterReveal = ref(false)

// Only show the "CODE applied…" line & Remove button once refresh is done
const showPromoStatus = computed(() => hasActiveDiscount.value && !isRefreshingAfterReveal.value)

const promoError = ref<string | null>(null)

// --- inventory / caps ---
function isInventorySoldOut(t: Ticket): boolean {
  // prefer server flag if present
  if (typeof (t as any).sold_out === 'boolean') return !!(t as any).sold_out
  if (t.quantity_available == null) return false
  return t.quantity_available <= 0
}

/** Per-ticket per-order limit (normal tickets or revealed ones that also set max_quantity) */
function isPerTicketOrderLimitReached(t: Ticket): boolean {
  const cap = typeof t.max_quantity === 'number' ? t.max_quantity : null
  if (cap == null) return false
  const current = qtyByTicket.value[t.id] ?? 0
  return current >= cap
}

// --- reveal-specific pool / order-caps ---
function isRevealContext(): boolean {
  return discountType() === 'reveal'
}

/** Reveal pool exhausted (0 remaining for this ticket) => SOLD OUT (badge) */
function isRevealSoldOut(t: Ticket): boolean {
  if (!isRevealContext()) return false
  const remain = remainingForTicket(t.id)
  const inCart = qtyByTicket.value[t.id] ?? 0
  // Sold out only when pool is zero AND you don't already hold one in the cart
  return remain === 0 && inCart === 0
}

/** Reveal order-level cap across eligible tickets => TICKET LIMIT REACHED (badge) */
function revealOrderLimitReached(): boolean {
  const d = session.value?.discount
  if (!d || (d.type || '').toLowerCase() !== 'reveal') return false
  const max = Number((d as any).max_uses_per_order || 0)
  if (!max) return false

  // Sum quantities in cart for eligible revealed tickets
  const eligible = Array.isArray(d.eligible_ticket_ids) && d.eligible_ticket_ids.length
    ? d.eligible_ticket_ids
    : Object.keys(qtyByTicket.value).map(n => Number(n))

  let used = 0
  for (const tid of eligible) used += qtyByTicket.value[tid] ?? 0
  return used >= max
}

// 1) Add these helpers (near your reveal helpers)
function revealedEligibleIds(): number[] {
  const d = session.value?.discount
  if (!d || (d.type || '').toLowerCase() !== 'reveal') return []

  // Prefer backend-provided eligible/applied sets
  if (Array.isArray(d.eligible_ticket_ids) && d.eligible_ticket_ids.length) return d.eligible_ticket_ids
  if (Array.isArray(d.applied_ticket_ids) && d.applied_ticket_ids.length) return d.applied_ticket_ids

  // Fallback: consider only tickets that are actually revealed in UI
  return Object.keys(ticketMap)
    .map(n => Number(n))
    .filter(id => {
      const tk = ticketMap[id]
      return !!tk && (isUnlocked(tk) || !!tk.was_revealed)
    })
}

function revealOrderLimitReachedFor(t?: Ticket): boolean {
  const d = session.value?.discount
  if (!d || (d.type || '').toLowerCase() !== 'reveal') return false

  const max = Number((d as any).max_uses_per_order || 0)
  if (!max) return false

  const eligible = revealedEligibleIds()
  if (!eligible.length) return false

  // Sum only revealed-eligible tickets
  let used = 0
  for (const tid of eligible) used += qtyByTicket.value[tid] ?? 0
  const reached = used >= max

  // If a ticket is passed, only enforce for revealed tickets
  if (t) {
    const thisIsRevealed = isUnlocked(t) || !!t.was_revealed
    return thisIsRevealed && reached
  }
  return reached
}
// One-time auto-switch guard (per session)
const hasSwitchedOnce = ref(false)
const autoSwitchKey = computed(() => `sj:autoSwitchRevealOnce:${session.value?.id ?? slug}`)

onMounted(() => {
  hasSwitchedOnce.value = sessionStorage.getItem(autoSwitchKey.value) === '1'
})



async function rehydrate(why: string = 'unknown') {
  try {

    // ✅ Add this guard at the top
    if (!shouldRehydrateNow(why)) {
      // skip any network/Stripe work unless truly coming back from background
      return
    }    
    // We're approved to rehydrate now
    rehydrateNeeded = false    
    
    // 1) Refresh the event data (tickets may have become sold out/ended)
    if (step.value === 'tickets') {
      try {
        await rebuildEventView(session.value?.id)
        
        // If we have a local cart, validate items against fresh ticket data
        if (isDirty.value && Object.keys(qtyByTicket.value).length > 0) {
          validateLocalCartAgainstEvent()
        }
      } catch (e) {
        console.warn('[widget] failed to refresh event data:', e)
      }
    }
    
    // 2) Refresh the checkout session if we have one
    if (session.value?.id) {
      const fresh = await fetchSessionById(session.value.id)
      const data = (fresh as any)?.data ?? fresh
      if (data && data.id) {
        // If we have local cart changes (isDirty), preserve them - don't overwrite with backend state
        // The local cart is authoritative until synced (at checkout or when voucher is applied)
        if (isDirty.value) {
          // Just update session metadata (status, discount, etc.) but keep our local cart
          session.value = {
            ...data,
            // Keep any existing ticket_groups from previous session
            ticket_groups: data.ticket_groups ?? (session.value as any)?.ticket_groups ?? []
          } as any
          // Don't call hydrateLocalQtyFromSession - keep local cart intact
          // Recalc client totals to keep UI consistent
          recalcClientTotals()
        } else {
          // No local changes - safe to use server state as authoritative
          await applySessionWithNotices(data)
          hydrateLocalQtyFromSession()
        }
      }
    }

    // 3) If we're on the payment step, ensure Stripe is mounted (iOS may purge iframes)
    if (step.value === 'pay') {
      await ensureStripe()
      const el = document.getElementById('payment-element')
      const hasIframe = !!el?.querySelector('iframe')
      if (isFreeCheckout.value) {
        await goToFreeCheckout()
      } else if (!hasIframe || checkoutStatus.value === 'requires_payment') {
        await startCheckout()
      }
    }

    // 4) Layout pass
    await runLayoutPass(why)

    console.debug('[widget] rehydrated:', { why, step: step.value })
  } catch (err) {
    console.error('[widget] rehydrate failed:', why, err)
    try {
      if (step.value === 'pay') await ensureStripe()
    } catch {}
    await runLayoutPass(`${why}-after-error`)
  }
}

function markSwitchedOnce () {
  hasSwitchedOnce.value = true
  sessionStorage.setItem(autoSwitchKey.value, '1')
}

// --- what to print on the UNLOCKED badge (reveal only) ---
function unlockedBadgeText(t: Ticket): string {
  if (!isRevealContext()) return 'Unlocked'

  // If the *order* cap (for revealed tickets) is hit, or this ticket's per-ticket cap is hit
  if (revealOrderLimitReachedFor(t) || isPerTicketOrderLimitReached(t)) {
    return 'Ticket Limit Reached'
  }

  // Only show "Sold Out" when the reveal pool is gone AND you don't already have one in cart
  if (isRevealSoldOut(t)) return 'Sold Out'

  return 'Unlocked'
}

watch(code, () => { if (promoError.value) promoError.value = null })
watch(() => session.value?.discount?.code, () => { promoError.value = null })

const hasActiveDiscount = computed(() => !!session.value?.discount?.code)
const appliedCode = computed(() => session.value?.discount?.code || '')
const currency = computed(() => session.value?.currency || event.value?.currency || 'AUD')

// Form inputs
const firstName = ref('')
const lastName  = ref('')
const email     = ref('')
const confirmEmail = ref('')
const acceptedTerms = ref(false)
const mailingList   = ref(true)
// Phone + postcode
const phoneDisplay     = ref('')
const fullPhoneObject  = ref<any | null>(null)
const phoneValid       = ref(false)

// NEW: track current country
const phoneCountryIso  = ref<'AU' | string>('AU')

const postcode = ref('')

// Is the phone Australian?
const isAuPhone = computed(() => phoneCountryIso.value === 'AU')

const onCountryChanged = (country: any) => {
  // vue-tel-input gives you an object; iso2 is the main 2-letter code
  phoneCountryIso.value = country?.iso2 || country?.countryCode || 'AU'
}

const errors = reactive({
  first: '',
  last: '',
  email: '',
  confirmEmail: '',
  phone: '',
  postcode: '',
  terms: '',
  general: '',
})

const touched = reactive({
  first: false,
  last: false,
  email: false,
  confirmEmail: false,
  phone: false,
  postcode: false,
  terms: false,
})

function resetErrors() {
  errors.first = ''
  errors.last = ''
  errors.email = ''
  errors.confirmEmail = ''
  errors.phone = ''
  errors.postcode = ''
  errors.terms = ''
  errors.general = ''
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
function validateBeforePlaceOrder(): boolean {
  resetErrors()

  // mark all as touched on full submit
  touched.first = true
  touched.last = true
  touched.email = true
  touched.confirmEmail = true
  touched.phone = true
  touched.postcode = true
  touched.terms = true

  validateFirstName()
  validateLastName()
  validateEmailField()
  validateConfirmEmailField()
  validatePhoneField()
  validatePostcodeField()
  validateTerms()

  if (
    errors.first ||
    errors.last ||
    errors.email ||
    errors.confirmEmail ||
    errors.phone ||
    errors.postcode ||
    errors.terms
  ) {
    errors.general = 'Please fix the highlighted fields.'
    return false
  }
  return true
}

function validateFirstName() {
  const v = firstName.value.trim()
  errors.first = v ? '' : 'Please enter your first name.'
}

function validateLastName() {
  const v = lastName.value.trim()
  errors.last = v ? '' : 'Please enter your last name.'
}

function validateEmailField() {
  const em = email.value.trim().toLowerCase()

  if (!em) {
    errors.email = 'Please enter your email address.'
  } else if (!EMAIL_RE.test(em)) {
    errors.email = 'Please enter a valid email address.'
  } else {
    errors.email = ''
  }

  // keep confirm in sync if it was already touched
  if (touched.confirmEmail) {
    validateConfirmEmailField()
  }
}

function validateConfirmEmailField() {
  const em  = email.value.trim().toLowerCase()
  const em2 = confirmEmail.value.trim().toLowerCase()

  if (!em2) {
    errors.confirmEmail = 'Please confirm your email address.'
  } else if (em2 !== em) {
    errors.confirmEmail = 'Email addresses do not match.'
  } else {
    errors.confirmEmail = ''
  }
}

function validatePhoneField() {
  // required for everyone, must be valid
  if (!phoneDisplay.value.trim()) {
    errors.phone = 'Please enter your phone number.'
  } else if (!phoneValid.value) {
    errors.phone = 'Please enter a valid phone number.'
  } else {
    errors.phone = ''
  }
}

function validatePostcodeField() {
  // Only validate when AU phone; otherwise clear error
  if (!isAuPhone.value) {
    errors.postcode = ''
    return
  }

  const pc = postcode.value.trim()
  if (!pc) {
    errors.postcode = 'Please enter your postcode.'
  } else if (!/^\d{4}$/.test(pc)) {
    errors.postcode = 'Postcode must be 4 digits.'
  } else {
    errors.postcode = ''
  }
}

function validateTerms() {
  errors.terms = acceptedTerms.value
    ? ''
    : 'You must accept the terms & conditions and privacy policy to continue.'
}

function onFirstNameBlur() {
  touched.first = true
  validateFirstName()
}

function onLastNameBlur() {
  touched.last = true
  validateLastName()
}

function onEmailBlur() {
  touched.email = true
  validateEmailField()
}

function onConfirmEmailBlur() {
  touched.confirmEmail = true
  validateConfirmEmailField()
}

function onPhoneBlur() {
  touched.phone = true
  validatePhoneField()
  // postcode depends on phone country, so also re-check postcode
  if (touched.postcode) validatePostcodeField()
}

function onPostcodeBlur() {
  touched.postcode = true
  validatePostcodeField()
}

function onTermsBlur() {
  touched.terms = true
  validateTerms()
}

function onPhoneValidate(evt: any) {
  phoneValid.value = !!evt.valid

  fullPhoneObject.value = {
    country: evt.country,
    country_code: evt.countryCode,
    country_calling_code: evt.countryCallingCode,
    formatted: evt.formatted,
    national_number: evt.nationalNumber,
    number: evt.number,
  }

  // As user types, if they've already blurred once, keep errors live
  if (touched.phone) {
    validatePhoneField()
    if (touched.postcode) validatePostcodeField()
  }
}


watch(firstName, () => {
  if (touched.first) validateFirstName()
})

watch(lastName, () => {
  if (touched.last) validateLastName()
})

watch(email, () => {
  if (touched.email) validateEmailField()
})

watch(confirmEmail, () => {
  if (touched.confirmEmail) validateConfirmEmailField()
})

// phoneDisplay changes via v-model
watch(phoneDisplay, () => {
  if (touched.phone) validatePhoneField()
})

// postcode changes via v-model
watch(postcode, () => {
  if (touched.postcode) validatePostcodeField()
})

watch(acceptedTerms, () => {
  if (touched.terms) validateTerms()
})

// If phone country changes from/to AU, re-validate postcode if already touched
watch(isAuPhone, () => {
  if (touched.postcode) validatePostcodeField()
})

// images - use active group's image if available, otherwise fallback
const heroUrl = computed(() => {
  // First priority: active group's image
  if (activeGroup.value?.image?.url) {
    return activeGroup.value.image.url
  }
  
  // Fallback to event-level images
  return (
    event.value?.popup_image?.url ||
    event.value?.landing_page_image?.url ||
    event.value?.email_header_image?.url ||
    event.value?.images?.[0]?.url ||
    null
  )
})
const heroAlt = computed(() => 
  activeGroup.value?.image?.url 
    ? `${event.value?.name || 'Event'} - ${activeGroup.value.label || 'Group'}`
    : event.value?.images?.[0]?.alt || event.value?.name || ''
)

/* =======================
   Read-more clamp
   ======================= */
const expanded = ref<Record<number, boolean>>({})
function toggleExpand(id: number) { expanded.value[id] = !expanded.value[id] }
function shouldClamp(html?: string | null) {
  if (!html) return false
  return html.replace(/<[^>]+>/g, '').trim().length > 180
}

function ticketStatus(t: Ticket) {
  // Reveal: handled by the badge; keep status area empty to avoid duplicate labels
  if (isRevealContext() && showUnlockedBadge(t)) return ''

  // Normal tickets:
  if (isPerTicketOrderLimitReached(t)) return 'Ticket Limit Reached'
  if (isInventorySoldOut(t) || t.sold_out) return 'Sold Out'
  if (t.sales_ended) return 'Sales Ended'
  return ''
}



/* =======================
   Date line
   ======================= */
const dateLine = computed(() => event.value?.display?.schedule || '')

/* =======================
   Address
   ======================= */
const normalizedAddress = computed(() => {
  const a = event.value?.address || {}
  // For UI: venue name + city/state only (no street address, no country)
  const text =
    a.formatted?.trim() ||
    [a.street_1, a.locality, a.state, a.postal_code]
      .filter(Boolean)
      .join(', ')
      .trim()
  // Use full address for Google Maps link if available
  const fullAddress = a.formatted_full || a.formatted || text
  const href =
    a.google_maps_link?.trim() ||
    (fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null)
  return { text: text || '', href: href || null }
})
const addressText = computed(() => normalizedAddress.value.text)
const addressHref = computed(() => normalizedAddress.value.href)

/* =======================
   Money helpers
   ======================= */
function cents(n?: number | null) { return typeof n === 'number' ? (n / 100).toFixed(2) : '0.00' }
function getQty(ticketId: number) { return qtyByTicket.value[ticketId] ?? 0 }

const pricing = computed<SessionPricing | undefined>(() => {
  return session.value?.pricing
      ?? (session.value && (session.value.tax_rate_percent != null || session.value.processing_fee_rate != null)
          ? { tax_rate_percent: session.value.tax_rate_percent, processing_fee_rate: session.value.processing_fee_rate }
          : undefined)
      ?? event.value?.pricing
})
const feeRate = computed(() =>
  session.value?.pricing?.processing_fee_rate ?? (event.value as any)?.pricing?.processing_fee_rate ?? 0
)
const taxRatePct = computed(() =>
  session.value?.pricing?.tax_rate_percent ?? (event.value as any)?.pricing?.tax_rate_percent ?? 0
)
const taxRate = computed(() => (taxRatePct.value > 0 ? taxRatePct.value / 100 : 0))

function unitBreakdownFromTicket(t: Ticket) {
  const unitEx = typeof t.price_cents_ex === 'number'
    ? t.price_cents_ex
    : Math.round(t.price_cents / (1 + taxRate.value))
  const feeEx    = Math.round(unitEx * feeRate.value)
  const taxCents = Math.round((unitEx + feeEx) * taxRate.value)
  const inc      = unitEx + feeEx + taxCents
  return { unitEx, feeEx, taxCents, inc }
}

function isDiscountEligible(t: Ticket): boolean {
  const d = session.value?.discount
  if (!d) return false

  if (discountType() === 'reveal') return false  // reveal never discounts

  if (d.scope === 'all') return true
  if (Array.isArray(d.eligible_ticket_ids)) return d.eligible_ticket_ids.includes(t.id)
  if (Array.isArray(d.applied_ticket_ids))  return d.applied_ticket_ids.includes(t.id)
  return false
}

function isPriceDiscount(): boolean {
  const t = discountType()
  return t === 'percent' || t === 'amount'
}

function discountedTicketPrice(t: Ticket) {
  // If it’s NOT a price-discount code, just return base
  if (!isPriceDiscount()) {
    return unitBreakdownFromTicket(t).inc
  }

  const base = unitBreakdownFromTicket(t).inc
  const d = session.value?.discount
  if (!d) return base
  if (!isDiscountEligible(t)) return base

  // ⬇️ NEW: If the next unit is no longer discounted because the cap was hit,
  // show the full base price immediately.
  if (!nextUnitIsDiscounted(t.id)) return base

  if (discountType() === 'percent') return Math.round(base * (1 - (d.value || 0) / 100))
  if (discountType() === 'amount')  return Math.max(0, base - (d.value || 0))
  return base
}

// Always show the *next* unit price on the ticket card.
// Use server preview ONLY if the next unit will actually be discounted.
// If the cap is exhausted, fall back to base inc price immediately.
function unitIncForTicket(t: Ticket) {
  // Price-discount context (percent/amount)
  if (isPriceDiscount()) {
    // If next unit is discounted, prefer server preview; else show base price
    if (nextUnitIsDiscounted(t.id)) {
      const p = session.value?.previews?.[t.id];
      if (p?.unit_effective_cents_inc != null) return p.unit_effective_cents_inc;
      // fallback (still discounted)
      return discountedTicketPrice(t);
    }
    // Cap exhausted → next unit is full price
    return unitBreakdownFromTicket(t).inc;
  }

  // Reveal codes don’t change price; show base inc price
  if (discountType() === 'reveal') {
    return unitBreakdownFromTicket(t).inc;
  }

  // No discount
  return unitBreakdownFromTicket(t).inc;
}

const priceIncFor = (t: Ticket): number => {
  if ((t.min_quantity ?? 1) > 1) {
    return unitBreakdownFromTicket(t).inc;
  }

  const p = (session.value?.previews as any)?.[String(t.id)];
  return (p?.unit_effective_cents_inc ?? null) ?? displayUnitInc(t);
};

const strikeIncFor = (t: Ticket): number | null => {
  if ((t.min_quantity ?? 1) > 1) {
    return null;
  }

  const p = (session.value?.previews as any)?.[String(t.id)];
  // server-computed compare-at (full price) when discounted; fallback to existing
  return (p?.unit_compare_at_cents_inc ?? null) ?? (displayStrikeInc(t) ?? null);
};

// Only show compare-at if the NEXT unit will actually be discounted
function showCompareAt(t: Ticket): boolean {
  if (!isPriceDiscount()) return false;
  if (!isDiscountEligible(t)) return false;
  if (!nextUnitIsDiscounted(t.id)) return false; // cap exhausted → hide strike

  const effectiveNext = unitIncForTicket(t);

  // Prefer server "compare at" for next unit
  const fromPreview = session.value?.previews?.[t.id]?.unit_compare_at_cents_inc;
  const compareAt = (fromPreview != null ? fromPreview : unitBreakdownFromTicket(t).inc);

  return compareAt > effectiveNext;
}

/* =======================
   Session helpers
   ======================= */

// Recalculate client-side totals (used when no voucher is applied)
function recalcClientTotals() {
  let subtotalInc = 0

  Object.entries(qtyByTicket.value).forEach(([tidStr, qty]) => {
    const tid = Number(tidStr)
    if (qty <= 0) return
    const t = ticketMap[tid]
    if (!t) return

    // No discount in local mode - just use base price
    const unitInc = unitBreakdownFromTicket(t).inc
    subtotalInc += unitInc * qty
  })

  // Calculate booking fee ONLY if there are PAID items (subtotal > 0)
  // Free items should not trigger a booking fee
  let bookingFeeInc = 0

  const bookingFeeEx = event.value?.pricing?.booking_fee_cents_ex
  if (subtotalInc > 0 && bookingFeeEx != null && bookingFeeEx > 0) {
    const bookingFeeTax = Math.round(bookingFeeEx * taxRate.value)
    bookingFeeInc = bookingFeeEx + bookingFeeTax
  }

  const totalInc = subtotalInc + bookingFeeInc

  clientTotals.value = {
    subtotal_cents_inc: subtotalInc,
    discount_cents_inc: 0, // No discount in local mode
    booking_fee_cents_inc: bookingFeeInc,
    total_cents_inc: totalInc,
    undiscounted_subtotal_cents_inc: subtotalInc,
    undiscounted_total_cents_inc: totalInc,
  }
}

function hydrateLocalQtyFromSession(s?: SessionResource) {
  const source = s ?? session.value
  if (!source?.items) return
  
  const map: Record<number, number> = {}
  source.items.forEach(i => {
    map[i.ticket_id] = (map[i.ticket_id] || 0) + (i.quantity || 0)
  })
  qtyByTicket.value = map
}

function linesForTicket(tid: number): SessionItem[] {
  return (session.value?.items || []).filter(i => i.ticket_id === tid)
}


function splitLines(tid: number) {
  const lines = linesForTicket(tid)
  const discounted = lines.filter(isDiscountedLine)
  const full       = lines.filter(l => !isDiscountedLine(l))
  return { discounted, full }
}

function hydrateBuyerFromSession(s: SessionResource) {
  if (s.first_name) firstName.value = s.first_name
  if (s.last_name)  lastName.value  = s.last_name
  if (s.email)      email.value     = s.email
  if (typeof s.accepted_terms === 'boolean') acceptedTerms.value = s.accepted_terms
  if (typeof s.mailing_list   === 'boolean') mailingList.value   = s.mailing_list
}
function applySession(updated: SessionResource) {
  if (!updated || !Array.isArray(updated.items)) {
    console.warn('Bad session payload:', updated)
    return
  }

  // Preserve ticket_groups from previous session if the new payload doesn't send them
  const prev = session.value as any | null
  const prevTicketGroups = prev?.ticket_groups || null
  const updatedAny = updated as any

  session.value = {
    ...updatedAny,
    ticket_groups: updatedAny.ticket_groups ?? prevTicketGroups ?? []
  } as any

  hydrateLocalQtyFromSession(updated)
  hydrateBuyerFromSession(updated)
  tagFullSpanOnSessionItems(session.value)

  // Clear dirty state - session is now authoritative
  isDirty.value = false
  clientTotals.value = null
  clearLocalCart() // Clear saved cart since server is now authoritative
}

// ---- Safe localStorage helpers ----
const safeGetItem = (key: string) => {
  try { return localStorage.getItem(key) } catch { return null }
}
const safeSetItem = (key: string, val: string) => {
  try { localStorage.setItem(key, val) } catch {}
}
const safeRemoveItem = (key: string) => {
  try { localStorage.removeItem(key) } catch {}
}

// ---- Local cart persistence ----
const cartStorageKey = computed(() => `sj:cart:${slug}`)

function saveLocalCart() {
  if (!isDirty.value) return
  const cart = { qtyByTicket: qtyByTicket.value, savedAt: Date.now() }
  safeSetItem(cartStorageKey.value, JSON.stringify(cart))
}

function loadLocalCart(): boolean {
  const raw = safeGetItem(cartStorageKey.value)
  if (!raw) return false
  
  try {
    const cart = JSON.parse(raw)
    // Only restore if saved within last 24 hours
    const maxAge = 24 * 60 * 60 * 1000 // 24 hours
    if (cart.savedAt && Date.now() - cart.savedAt > maxAge) {
      safeRemoveItem(cartStorageKey.value)
      return false
    }
    
    if (cart.qtyByTicket && Object.keys(cart.qtyByTicket).length > 0) {
      qtyByTicket.value = cart.qtyByTicket
      isDirty.value = true
      recalcClientTotals()
      return true
    }
  } catch {}
  return false
}

function clearLocalCart() {
  safeRemoveItem(cartStorageKey.value)
}

/**
 * Validate local cart items against fresh event data.
 * Removes unavailable tickets and shows notices.
 */
function validateLocalCartAgainstEvent() {
  const removedTickets: string[] = []
  const adjustedTickets: { name: string; newQty: number }[] = []
  const newQty = { ...qtyByTicket.value }
  
  for (const [tidStr, qty] of Object.entries(qtyByTicket.value)) {
    if (qty <= 0) continue
    
    const tid = Number(tidStr)
    const ticket = ticketMap[tid]
    
    if (!ticket) {
      // Ticket no longer exists
      delete newQty[tid]
      removedTickets.push(`Ticket #${tid}`)
      continue
    }
    
    // Check if ticket is still sellable
    const isSellable = !ticket.sold_out && 
                       !ticket.sales_ended && 
                       ticket.sellable !== false &&
                       (ticket.quantity_available === null || ticket.quantity_available > 0)
    
    if (!isSellable) {
      delete newQty[tid]
      const reason = ticket.sales_ended ? 'Sales have ended' : 
                     ticket.sold_out ? 'Sold out' : 'No longer available'
      removedTickets.push(`${ticket.name}: ${reason}`)
      continue
    }
    
    // Check if quantity exceeds available stock
    if (ticket.quantity_available !== null && qty > ticket.quantity_available) {
      if (ticket.quantity_available <= 0) {
        delete newQty[tid]
        removedTickets.push(`${ticket.name}: Sold out`)
      } else {
        newQty[tid] = ticket.quantity_available
        adjustedTickets.push({ name: ticket.name, newQty: ticket.quantity_available })
      }
    }
  }
  
  // Update cart if anything changed
  if (removedTickets.length > 0 || adjustedTickets.length > 0) {
    qtyByTicket.value = newQty
    recalcClientTotals()
    saveLocalCart()
    
    // Show notices
    const newNotices: SessionNotice[] = []
    
    for (const msg of removedTickets) {
      newNotices.push({
        code: 'ticket_removed_unavailable',
        message: msg,
      })
    }
    
    for (const { name, newQty } of adjustedTickets) {
      newNotices.push({
        code: 'ticket_quantity_reduced',
        message: `${name}: Only ${newQty} available. Quantity adjusted.`,
      })
    }
    
    // Add summary notice
    const totalChanges = removedTickets.length + adjustedTickets.length
    if (totalChanges > 0) {
      const removedCount = removedTickets.length
      const adjustedCount = adjustedTickets.length
      
      let summary = ''
      if (removedCount > 0) {
        summary += removedCount === 1 ? '1 × Ticket Removed' : `${removedCount} × Tickets Removed`
      }
      if (adjustedCount > 0) {
        if (summary) summary += ', '
        summary += adjustedCount === 1 ? '1 × Quantity Adjusted' : `${adjustedCount} × Quantities Adjusted`
      }
      
      newNotices.unshift({
        code: 'cart_updated_summary',
        message: summary,
      })
    }
    
    notices.value = [...notices.value, ...newNotices]
    
    // Scroll to top of ticket section
    scrollToTicketSection()
  }
}

// Watch for cart changes and persist
watch(qtyByTicket, () => {
  if (isDirty.value) {
    saveLocalCart()
  }
}, { deep: true })

/**
 * Scroll to top of ticket section to show notices.
 * Used when backend returns notices (e.g., during checkout sync/validate).
 */
function scrollToTicketSection() {
  // Small delay to ensure DOM is updated before scrolling
  setTimeout(() => {
    // First, try the modal scroll container (when widget is in a modal)
    const modalScroll = document.querySelector<HTMLElement>('.modal-scroll')
    if (modalScroll && modalScroll.scrollTop > 0) {
      modalScroll.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    // Second, try the widget content area - this is the main scrollable container
    const contentArea = document.querySelector<HTMLElement>('.js-ticket-content-area')
    if (contentArea) {
      // Always scroll this - it's the main container
      if (contentArea.scrollTop > 0) {
        contentArea.scrollTo({ top: 0, behavior: 'smooth' })
        // Also set directly as fallback
        contentArea.scrollTop = 0
        return
      }
    }

    // Third, try walking up from .js-ticket-content-area to find parent scrollable
    if (contentArea) {
      let parent = contentArea.parentElement
      let level = 0
      while (parent && level < 10) {
        const style = getComputedStyle(parent)
        const overflow = style.overflow + style.overflowY
        if ((overflow.includes('scroll') || overflow.includes('auto')) && (parent as HTMLElement).scrollTop > 0) {
          (parent as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
        parent = parent.parentElement
        level++
      }
    }

    // Fourth, try walking up from ticket-groups to find scrollable container
    let el: Element | null = document.querySelector('.ticket-groups')
    while (el) {
      const style = getComputedStyle(el)
      const overflow = style.overflow + style.overflowY
      if ((overflow.includes('scroll') || overflow.includes('auto')) && (el as HTMLElement).scrollTop > 0) {
        (el as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      el = el.parentElement
    }
    
    // Fifth, try the step host container and walk up
    const stepHost = document.querySelector<HTMLElement>('.step-pane')
    if (stepHost) {
      let parent = stepHost.parentElement
      while (parent) {
        const style = getComputedStyle(parent)
        const overflow = style.overflow + style.overflowY
        if ((overflow.includes('scroll') || overflow.includes('auto')) && (parent as HTMLElement).scrollTop > 0) {
          parent.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
        parent = parent.parentElement
      }
    }

    // Don't scroll window - only scroll within widget containers
    // If no scrollable container found, silently do nothing
  }, 200)
}

function clearCartSessionStorage() {
  const key = `sj:session:${slug}`
  safeRemoveItem(key)
}

// onMounted
// Auto-apply voucher from URL or postMessage
const pendingVoucherCode = ref<string | null>(null)

async function autoApplyVoucher(voucherCode: string) {
  if (!voucherCode || !session.value || hasActiveDiscount.value) return
  
  code.value = voucherCode
  applyingCode.value = true
  promoError.value = null
  
  try {
    const updated = await applySessionDiscount(session.value.id, voucherCode)
    applySessionWithNotices((updated as any)?.data ?? updated)
    
    const reveals = discountType() === 'reveal'
    if (reveals) {
      isRefreshingAfterReveal.value = true
      await rebuildEventView(session.value?.id)
      isRefreshingAfterReveal.value = false
      jumpToFirstPromoGroup(true)
    } else {
      jumpToFirstPromoGroup(true)
    }
  } catch (e: any) {
    const m = e?.response?.data?.message ?? e?.response?.data?.errors?.code?.[0] ?? 'Invalid or expired code.'
    promoError.value = m
  } finally {
    applyingCode.value = false
  }
}

// Listen for postMessage from parent window (for iframe embedding)
function handlePostMessage(event: MessageEvent) {
  // Validate origin if needed for security
  // if (event.origin !== 'https://your-allowed-domain.com') return
  
  const data = event.data
  if (!data || typeof data !== 'object') return
  
  // Handle voucher code message
  if (data.type === 'applyVoucher' && data.code) {
    autoApplyVoucher(data.code)
  }
  
  // Handle open widget message (for iframe)
  if (data.type === 'openWidget') {
    // Widget is always "open" in the iframe context, but we could scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

onMounted(async () => {
  isBooting.value = true;
  
  // Listen for postMessage (for iframe communication)
  window.addEventListener('message', handlePostMessage)
  
  try {
    const ev = await fetchEventBySlug(slug);
    event.value = ev.data;

    ev.data.groups.forEach(g =>
      g.tickets.forEach(t => {
        ticketToGroup[t.id] = g.id;
        ticketMap[t.id] = t;
      })
    );

    const firstSellable = ev.data.groups.find(isGroupSellable);
    currentGroupId.value = (firstSellable || ev.data.groups[0] || { id: null }).id;

    // 👇 check localStorage safely
    const key = `sj:session:${slug}`;
    const savedId = safeGetItem(key);

    let s;
    if (savedId) {
      try {
        s = await fetchSessionById(savedId);
      } catch (e) {
        console.warn('old session invalid, creating new', e);
        s = await createSessionForEvent(slug);
      }
    } else {
      s = await createSessionForEvent(slug);
    }

    applySession(s);
    safeSetItem(key, s.id);
    
    // Try to restore local cart if we have one saved
    // Only restore if the session has no items (server cart is empty)
    if (!s.items?.length) {
      loadLocalCart()
    }
    
    // Check for voucher code in URL params (?voucher=CODE or ?promo=CODE)
    const urlParams = new URLSearchParams(window.location.search)
    const voucherFromUrl = urlParams.get('voucher') || urlParams.get('promo') || urlParams.get('code')
    if (voucherFromUrl && !hasActiveDiscount.value) {
      pendingVoucherCode.value = voucherFromUrl
    }
  } finally {
    await nextTick();
    try { adjustQtyWidths(); } catch {}
    try { syncHeaderHeight(); } catch {}
    try { syncContentArea(); } catch {}
    isBooting.value = false;
    
    // Auto-apply voucher after boot completes
    if (pendingVoucherCode.value) {
      await autoApplyVoucher(pendingVoucherCode.value)
      pendingVoucherCode.value = null
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handlePostMessage)
})



// Client-side notice fallback when price-discount caps flip
// Replace your existing watcher with this complete version
watch(
  () => [session.value?.discount?.type, session.value?.caps],
  () => {
    const dType = discountType();
    const caps  = session.value?.caps;

    // Only synthesize in price-discount contexts
    if (!caps || !['percent', 'amount'].includes(dType)) {
      return; // leave server notices as-is
    }

    const globalOut =
      typeof caps.discounted_units_remaining === 'number'
        ? caps.discounted_units_remaining <= 0
        : false;

    const tmp: SessionNotice[] = [];

    const per = caps.remaining_by_ticket || {};
    Object.keys(per).forEach(k => {
      const tid = Number(k);
      if (per[tid] === 0 && isTicketEligibleForPriceDiscount(tid)) {
        tmp.push({
          code: 'cap_exhausted_ticket',
          message: 'Discount limit reached for this ticket.',
          ticket_id: tid,
        });
      }
    });

    if (globalOut) {
      tmp.push({
        code: 'cap_exhausted_global',
        message: 'Discount limit reached. Further tickets will be full price.',
      });
    }

    const remOrder = session.value?.discount?.remaining_uses_in_order;
    if (typeof remOrder === 'number' && remOrder <= 0) {
      tmp.push({
        code: 'cap_exhausted_global',
        message: 'Discount limit reached. Further tickets will be full price.',
      });
    }

    if (tmp.length === 0) return;

    const serverNotices = Array.isArray(session.value?.notices)
      ? session.value!.notices!
      : [];

    const uniqueKey = n => `${n.code}:${n.ticket_id ?? 'all'}`;
    const map = new Map();
    [...serverNotices, ...tmp].forEach(n => map.set(uniqueKey(n), n));

    notices.value = Array.from(map.values());
  },
  { deep: true, immediate: true }
);

/* =======================
   Qty & promo mutations
   ======================= */

// --- Promo "Unlocked" helpers -----------------------------------------------
const hasActivePromo = computed(() => !!session.value?.discount?.code)

function isUnlocked(t: Ticket): boolean {
  const d = session.value?.discount
  if (!d || d.type !== 'reveal') return false

  // Only show for tickets explicitly unlocked by the reveal code:
  if (Array.isArray(d.applied_ticket_ids) && d.applied_ticket_ids.includes(t.id)) return true
  if (Array.isArray(d.eligible_ticket_ids) && d.eligible_ticket_ids.includes(t.id)) return true
  return false
}

function showUnlockedBadge(t: Ticket): boolean {
  // Show the badge if this ticket is either unlocked by the active reveal code
  // OR it was previously revealed server-side.
  return isUnlocked(t) || !!t.was_revealed
}

function hasAnyTickets(g: Group): boolean {
  return Array.isArray(g?.tickets) && g.tickets.some(isVisible)
}

function groupHasSellable(g: Group): boolean {
  return hasAnyTickets(g) && (g.tickets || []).some(isEffectivelySellable)
}

function isGroupSellable(g: Group): boolean {
  if (!g?.tickets?.length) return false
  if (typeof g.sellable_ticket_count === 'number') return g.sellable_ticket_count > 0
  return g.tickets.some(isEffectivelySellable)
}


const allGroups = computed(() => event.value?.groups || [])

const allGroupsSorted = computed(() => {
  const list = allGroups.value.map((g, i) => ({
    g,
    idx: i,
    sellable: isGroupSellable(g),
  }))
  list.sort((a, b) => {
    if (a.sellable !== b.sellable) return a.sellable ? -1 : 1
    return a.idx - b.idx
  })
  return list.map(x => x.g)
})

const visibleGroupsSorted = computed(() => {
  const groups = (event.value?.groups ?? []).filter(hasAnyTickets)
  return groups
    .map((g, idx) => ({ g, idx, sellable: groupHasSellable(g) }))
    .sort((a, b) => (a.sellable !== b.sellable ? (a.sellable ? -1 : 1) : a.idx - b.idx))
    .map(x => x.g)
})


const userPicked = ref(false)
function pickGroup(id:number){ currentGroupId.value = id; userPicked.value = true }

// choose tab automatically the first time / when data changes (unless user picked)
watch(visibleGroupsSorted, (list) => {
  if (!list.length) { currentGroupId.value = null; return }

  const current = list.find(g => g.id === currentGroupId.value)
  if (userPicked.value && current) return

  const firstSellable = list.find(groupHasSellable)
  const target = firstSellable ?? list[0]
  if (!current || (!groupHasSellable(current) && firstSellable)) {
    currentGroupId.value = target.id
  }
}, { immediate: true })

const hasGroupTabs = computed(() => visibleGroupsSorted.value.length > 1)

const activeGroup = computed(() =>
  visibleGroupsSorted.value.find(g => g.id === currentGroupId.value) ||
  visibleGroupsSorted.value[0] || null
)

watch(visibleGroupsSorted, (list) => {
  if (!list.length) { currentGroupId.value = null; return }
  const exists = list.some(g => g.id === currentGroupId.value)
  if (!exists) {
    const firstSellable = list.find(groupHasSellable)
    currentGroupId.value = (firstSellable ?? list[0]).id
  }
}, { immediate: true })

// --- Groups --------------------------------------------------------------
const sellableGroups = computed(() =>
  (event.value?.groups || []).filter(g => (g.tickets || []).some(t => isSellable(t)))
)
const fallbackGroups = computed(() =>
  (event.value?.groups || []).filter(g => (g.tickets || []).length > 0)
)
const displayGroups = computed(() =>
  sellableGroups.value.length ? sellableGroups.value : fallbackGroups.value
)

// Groups that actually have something sellable
const groupsWithSellableTickets = computed(() =>
  (event.value?.groups || []).filter(groupHasSellable)
)

// Used by template to decide whether to show tickets list or "No tickets" message
const hasAvailableTickets = computed(() => groupsWithSellableTickets.value.length > 0)


async function rebuildEventView(withSessionId?: string) {
  const ev2 = withSessionId
    ? await fetchEventBySlug(slug, { params: { session_id: withSessionId } })
    : await fetchEventBySlug(slug)

  event.value = ev2.data

  // rebuild maps
  const newTicketToGroup: Record<number, number> = {}
  const newTicketMap: Record<number, Ticket> = {}
  ;(ev2.data.groups || []).forEach((g: Group) =>
    (g.tickets || []).forEach((t: Ticket) => {
      newTicketToGroup[t.id] = g.id
      newTicketMap[t.id] = t
    })
  )
  Object.assign(ticketToGroup, newTicketToGroup)
  Object.assign(ticketMap, newTicketMap)
}


async function increment(t: Ticket) {
  if (!isEffectivelySellable(t) || pendingByTicket.value[t.id]) return

  const step = t.min_quantity && t.min_quantity > 1 ? t.min_quantity : 1
  const oldQty = getQty(t.id)

  // respect hard caps (per-ticket + stock)
  let hardMax = Number.POSITIVE_INFINITY
  if (t.max_quantity != null)       hardMax = Math.min(hardMax, t.max_quantity)
  if (t.quantity_available != null) hardMax = Math.min(hardMax, t.quantity_available)

  // ✅ allow Infinity; only block if even a single step isn't possible
  if (hardMax < step) return

  let target = oldQty === 0 ? step : oldQty + step
  target = Math.min(target, hardMax)
  target = Math.floor(target / step) * step
  const delta = target - oldQty
  if (delta <= 0) return

  // If voucher is applied, use backend (server-driven cart)
  if (hasActiveDiscount.value && session.value) {
    pendingByTicket.value = { ...pendingByTicket.value, [t.id]: true }
    try {
      // Add delta new units; backend will split discounted/full as needed
      const updated = await addSessionItem(session.value.id, { ticket_id: t.id, quantity: delta })
      applySessionWithNotices(updated)
    } finally {
      pendingByTicket.value = { ...pendingByTicket.value, [t.id]: false }
    }
  } else {
    // No voucher: update local cart only (frontend-driven)
    qtyByTicket.value = { ...qtyByTicket.value, [t.id]: target }
    isDirty.value = true
    recalcClientTotals()
  }
}

// --- CAP-AWARE HELPERS (next unit pricing + messages) -----------------------

function isTicketEligibleForPriceDiscount(tid: number): boolean {
  const d = session.value?.discount
  if (!d) return false
  if (discountType() !== 'percent' && discountType() !== 'amount') return false

  if (d.scope === 'all') return true
  if (Array.isArray(d.eligible_ticket_ids)) return d.eligible_ticket_ids.includes(tid)
  if (Array.isArray(d.applied_ticket_ids))  return d.applied_ticket_ids.includes(tid)
  return false
}

// Is next unit discounted? (preview exists AND effective < compare-at)
function nextUnitIsDiscounted(ticketId: number) {
  // Only for price-discount codes and eligible tickets
  if (!isPriceDiscount() || !isTicketEligibleForPriceDiscount(ticketId)) return false;

  // 🔐 Order-level cap from the discount object itself
  const remOrder = session.value?.discount?.remaining_uses_in_order;
  if (typeof remOrder === 'number' && remOrder <= 0) return false;

  // Hard-stop if the cap is exhausted (global OR per-ticket) from caps
  const caps = session.value?.caps;
  if (caps) {
    if (typeof caps.discounted_units_remaining === 'number' && caps.discounted_units_remaining <= 0) {
      return false;
    }
    const rb = caps.remaining_by_ticket?.[ticketId];
    if (typeof rb === 'number' && rb <= 0) return false;
  }

  // Then rely on preview math for the *next* unit
  const p = session.value?.previews?.[ticketId];
  return !!(p && p.unit_compare_at_cents_inc && p.unit_effective_cents_inc < p.unit_compare_at_cents_inc);
}

/**
 * Returns a suggested compare-at for the NEXT unit. Prefer backend previews if present.
 */
function nextUnitCompareAtInc(t: Ticket): number {
  // If server gave us a compare_at for the next unit, prefer it
  const preview = session.value?.previews?.[t.id]
  if (preview?.unit_compare_at_cents_inc != null) return preview.unit_compare_at_cents_inc

  // Fallback to base (undiscounted) unit inc locally
  return unitBreakdownFromTicket(t).inc
}

/**
 * Returns true if this ticket is eligible for the discount BUT the limit has been reached.
 * Used to show "Discount limit reached" notice on eligible tickets even when not in cart.
 */
function isDiscountLimitReachedForTicket(t: Ticket): boolean {
  // Only applies to price discounts (amount/percent)
  if (!isPriceDiscount()) return false
  
  // Check if this ticket is eligible for the discount
  if (!isTicketEligibleForPriceDiscount(t.id)) return false
  
  // Don't show on free tickets
  if ((unitBreakdownFromTicket(t).inc || 0) === 0) return false
  
  // Check if the order-level cap is exhausted
  const remOrder = session.value?.discount?.remaining_uses_in_order
  if (typeof remOrder === 'number' && remOrder <= 0) return true
  
  // Check global cap
  const caps = session.value?.caps
  if (caps) {
    if (typeof caps.discounted_units_remaining === 'number' && caps.discounted_units_remaining <= 0) {
      return true
    }
    // Per-ticket cap
    const rb = caps.remaining_by_ticket?.[t.id]
    if (typeof rb === 'number' && rb <= 0) return true
  }
  
  return false
}


async function decrement(t: Ticket) {
  if (pendingByTicket.value[t.id]) return

  const step = t.min_quantity && t.min_quantity > 1 ? t.min_quantity : 1
  const oldQty = getQty(t.id)
  if (oldQty <= 0) return

  let target = Math.max(0, oldQty - step)
  target = Math.floor(target / step) * step

  // If voucher is applied, use backend (server-driven cart)
  if (hasActiveDiscount.value && session.value) {
    const toRemove = oldQty - target
    if (toRemove <= 0) return

    pendingByTicket.value = { ...pendingByTicket.value, [t.id]: true }
    try {
      let remaining = toRemove
      const { full, discounted } = splitLines(t.id)

      // 1) Remove from FULL-PRICE lines first
      for (const line of full) {
        if (remaining <= 0) break
        const take = Math.min(remaining, line.quantity)
        const newQty = line.quantity - take
        const updated = await updateSessionItemQty(session.value.id, line.id, newQty)
        applySessionWithNotices(updated)
        remaining -= take
      }

      // 2) Then remove from DISCOUNTED lines
      for (const line of discounted) {
        if (remaining <= 0) break
        const take = Math.min(remaining, line.quantity)
        const newQty = line.quantity - take
        const updated = await updateSessionItemQty(session.value.id, line.id, newQty)
        applySessionWithNotices(updated)
        remaining -= take
      }
    } finally {
      pendingByTicket.value = { ...pendingByTicket.value, [t.id]: false }
    }
  } else {
    // No voucher: update local cart only (frontend-driven)
    if (target === 0) {
      const newQty = { ...qtyByTicket.value }
      delete newQty[t.id]
      qtyByTicket.value = newQty
    } else {
      qtyByTicket.value = { ...qtyByTicket.value, [t.id]: target }
    }
    isDirty.value = true
    recalcClientTotals()
  }
}

const lineByTicketId = computed<Record<number, SessionItem>>(() => {
  const m: Record<number, SessionItem> = {}
  const items = session.value?.items
  if (Array.isArray(items)) items.forEach(i => (m[i.ticket_id] = i))
  return m
})

function ticketHasPriceDiscount(t: Ticket): boolean {
  return isPriceDiscount() && isDiscountEligible(t)
}

function ticketHasReveal(t: Ticket): boolean {
  return isUnlocked(t)
}

function ticketHasPromo(t: Ticket): boolean {
  // Treat reveal and price-discount as “promo”, but only if the ticket is visible.
  const revealed = isUnlocked(t) || !!t.was_revealed
  return (revealed || ticketHasPriceDiscount(t)) && isVisible(t)
}

function groupHasPromo(g: Group): boolean {
  return (g?.tickets || []).some(ticketHasPromo)
}

function firstPromoGroupId(): number | null {
  const g = visibleGroupsSorted.value.find(groupHasPromo) // ⬅️ use visibility-aware list
  return g ? g.id : null
}


watch(
  () => [event.value?.groups, session.value?.discount],
  () => { jumpToFirstPromoGroup() },
  { deep: true }
)

function discountAppliesTo(t: Ticket): boolean {
  const d = session.value?.discount
  if (!d) return false
  const typ = discountType()
  if (typ === 'reveal') return isUnlocked(t) || !!t.was_revealed
  if (typ === 'percent' || typ === 'amount') return isDiscountEligible(t)
  return false
}

function groupHasApplicable(g: Group): boolean {
  return (g?.tickets || []).some(t => isVisible(t) && discountAppliesTo(t))
}

function groupHasApplicableSellable(group: any): boolean {
  if (!group || !Array.isArray(group.tickets)) return false

  const type = discountType() // 'amount' | 'percent' | 'reveal' | null

  return (group.tickets as Ticket[]).some((t) => {
    // If this is a reveal code, we care about "unlocked" tickets
    if (type === 'reveal') {
      // use your existing unlock logic + only consider tickets that can actually be bought
      return showUnlockedBadge(t) && !!t.effective_sellable
    }

    // For amount/percent, use the same logic as the green discount border
    return isDiscountContextForTicket(t)
  })
}

function firstApplicableTargetId(): number | null {
  const list = visibleGroupsSorted.value
  const sellable = list.find(groupHasApplicableSellable)
  if (sellable) return sellable.id
  const applicable = list.find(groupHasApplicable)
  return applicable ? applicable.id : null
}

function jumpToFirstPromoGroup(force = false) {
  // only when a discount exists
  if (!session.value?.discount?.code) return
  if (!force && hasSwitchedOnce.value) return

  nextTick(() => {
    const targetId = firstApplicableTargetId()
    if (!targetId) return

    const list = visibleGroupsSorted.value
    const current = list.find(g => g.id === currentGroupId.value)
    const currentOk = current ? groupHasApplicableSellable(current) : false

    // switch if: no current tab, or current tab lacks an applicable & sellable ticket
    if (!current || !currentOk) {
      currentGroupId.value = targetId
      userPicked.value = false
      markSwitchedOnce()
    }
  })
}

const displayedTicketsForActiveGroup = computed<Ticket[]>(() => {
  const group = activeGroup.value
  if (!group) return []

  // groups from API have `tickets: Ticket[]`
  return Array.isArray(group.tickets) ? group.tickets : []
})

const sortedTicketsForActiveGroup = computed(() => {
  if (!activeGroup.value) return []
  
  return [...activeGroup.value.tickets].sort((a, b) => {
    // Always push sold out / sales ended tickets to the end
    const aUnavailable = a.sold_out || a.sales_ended || isInventorySoldOut(a)
    const bUnavailable = b.sold_out || b.sales_ended || isInventorySoldOut(b)
    if (aUnavailable !== bUnavailable) {
      return aUnavailable ? 1 : -1 // unavailable goes to end
    }
    
    // When a discount is active, prioritize:
    // 1. Revealed tickets (for reveal codes)
    // 2. Discounted tickets (for amount/percent codes, but NOT free tickets)
    if (hasActiveDiscount.value) {
      // Check if ticket is revealed by a reveal code
      const aIsRevealed = isUnlocked(a) || !!a.was_revealed
      const bIsRevealed = isUnlocked(b) || !!b.was_revealed
      if (aIsRevealed !== bIsRevealed) {
        return aIsRevealed ? -1 : 1 // revealed first
      }
      
      // Check if ticket has a price discount (not free)
      const aIsDiscounted = isDiscountContextForTicket(a) && unitBreakdownFromTicket(a).inc > 0
      const bIsDiscounted = isDiscountContextForTicket(b) && unitBreakdownFromTicket(b).inc > 0
      if (aIsDiscounted !== bIsDiscounted) {
        return bIsDiscounted ? 1 : -1 // discounted first
      }
    }

    // Primary: backend sort_order
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order
    }

    // Secondary: name (same as backend)
    return a.name.localeCompare(b.name)
  })
})

function isNormalLimitReached(t: Ticket): boolean {
  return !isRevealContext() && isPerTicketOrderLimitReached(t)
}

async function applyCode() {
  if (!code.value || !session.value || hasActiveDiscount.value) return
  applyingCode.value = true
  promoError.value = null
  try {
    // If cart is dirty (local mode), sync to backend first before applying discount
    if (isDirty.value) {
      const itemsToSync = Object.entries(qtyByTicket.value)
        .map(([tid, qty]) => ({ ticket_id: Number(tid), quantity: qty }))
        .filter(i => i.quantity > 0)

      const syncRes = await syncSessionItems(session.value.id, itemsToSync)
      const syncData = (syncRes as any).data ?? syncRes
      applySessionWithNotices(syncData)
      isDirty.value = false
      clientTotals.value = null
      clearLocalCart()
    }

    const updated = await applySessionDiscount(session.value.id, code.value)
    applySessionWithNotices(updated)
    code.value = updated?.discount?.code || code.value

    const t = discountType()
    const reveals = t === 'reveal' && ((updated?.discount as any)?.reveals_hidden_tickets ?? true)

    if (reveals) {
      isRefreshingAfterReveal.value = true
      await rebuildEventView(updated.id)
      isRefreshingAfterReveal.value = false
      jumpToFirstPromoGroup(true)      // force once after reveal
    } else {
      jumpToFirstPromoGroup(true)      // ✅ now also jump for percent/amount codes
    }
  } catch (e: any) {
    const m = e?.response?.data?.message ?? e?.response?.data?.errors?.code?.[0] ?? 'Invalid or expired code.'
    promoError.value = m
  } finally {
    applyingCode.value = false
  }
}

function remainingUserStock(t: Ticket): number | null {
  if (typeof t.quantity_available !== 'number') return null
  const inCart = qtyByTicket.value[t.id] ?? 0
  return t.quantity_available - inCart
}

/** Should we show the SOLD OUT badge for normal tickets? */
function showSoldOutBadge(t: Ticket): boolean {
  if (isRevealContext()) return false
  // Don't show badge if ticket is greyed out (not sellable)
  if (!isEffectivelySellable(t)) return false
  // Only show badge when ticket is sellable but user can't add more (used all stock)
  const rem = remainingUserStock(t)
  return rem != null && rem <= 0
}

function discountType(): 'percent'|'amount'|'reveal'|'' {
  const raw = session.value?.discount?.type
  return typeof raw === 'string' ? (raw as string).toLowerCase() as any : ''
}

async function removeCode() {
  if (!session.value) return
  const wasReveal = discountType() === 'reveal'

  applyingCode.value = true
  promoError.value = null
  try {
    const updated = await removeSessionDiscount(session.value.id)
    applySessionWithNotices((updated as any)?.data ?? updated) // <- always hard-replace
    code.value = ''
    if (wasReveal) {
      await rebuildEventView(undefined) // hide revealed tickets again
    }
  } catch {
    promoError.value = 'Could not remove code. Please try again.'
  } finally {
    applyingCode.value = false
  }
}

// --- Sellability & discount eligibility helpers ----------------------------
function isWithinSalesWindow(t: Ticket): boolean {
  const tz = (event.value as any)?.timezone || dayjs.tz.guess()
  const now = dayjs().tz(tz)
  const start = t.sales_window?.start ? dayjs.utc(t.sales_window.start).tz(tz) : null
  const end   = t.sales_window?.end   ? dayjs.utc(t.sales_window.end).tz(tz)   : null
  const afterStart = !start || now.isSame(start) || now.isAfter(start)
  const beforeEnd  = !end   || now.isBefore(end)
  return afterStart && beforeEnd
}

function isSellable(t: Ticket): boolean {
  const explicit = typeof t.sellable === 'boolean' ? t.sellable : null
  const inferred =
    !t.sold_out &&
    !t.sales_ended &&
    t.on_sale !== false &&
    (t.quantity_available == null || t.quantity_available > 0) &&
    isWithinSalesWindow(t)
  return explicit ?? inferred
}

function isVisible(t: Ticket): boolean {
  // Prefer server truth
  if (typeof (t as any).effective_visible === 'boolean') {
    return !!(t as any).effective_visible
  }
  // Fallback: hide if hidden unless it's unlocked by a reveal code
  if (typeof (t as any).is_hidden === 'boolean') {
    return !(t as any).is_hidden || isUnlocked(t)
  }
  return true
}

function isEffectivelySellable(t: Ticket): boolean {
  // Prefer server truth if provided
  if (typeof (t as any).effective_sellable === 'boolean') {
    return !!(t as any).effective_sellable
  }

  const visible = isVisible(t)

  // If it's revealed (via code) or previously revealed, allow sell even if on_sale === false
  const revealed = isUnlocked(t) || !!t.was_revealed

  // Baseline constraints we still respect
  const hasStock   = t.quantity_available == null || t.quantity_available > 0
  const inWindow   = isWithinSalesWindow(t)
  const notClosed  = !t.sold_out && !t.sales_ended

  if (revealed && visible) {
    // Don’t require t.on_sale or t.sellable when revealed
    return notClosed && hasStock && inWindow
  }

  // Fallback to your normal rules for public tickets
  const explicit = typeof t.sellable === 'boolean' ? t.sellable : null
  const inferred =
    notClosed &&
    (t.on_sale !== false) &&
    hasStock &&
    inWindow &&
    visible

  return explicit ?? inferred
}

// Returns the price to display for "one more" unit of this ticket
function displayUnitInc(t: Ticket) {
  // 🚫 Multi-pack tickets: never show discounted unit price
  if ((t.min_quantity ?? 1) > 1) {
    return unitBreakdownFromTicket(t).inc
  }

  // If a price-discount exists but the next unit is NOT discounted, show base price immediately
  if (isPriceDiscount() && !nextUnitIsDiscounted(t.id)) {
    return unitBreakdownFromTicket(t).inc;
  }
  // Otherwise, use the preview-driven next-unit price (handles discounted + normal cases)
  return unitIncForTicket(t);
}

// Strike/compare-at price (only if preview says there's a lower promo)
function displayStrikeInc(t: Ticket) {
  // 🚫 Never show strike price on multi-pack / min-qty tickets
  if ((t.min_quantity ?? 1) > 1) {
    return null
  }

  // Show strike ONLY when a price-discount applies to the NEXT unit
  if (!isPriceDiscount() || !isTicketEligibleForPriceDiscount(t.id) || !nextUnitIsDiscounted(t.id)) {
    return null;
  }

  // Prefer server-provided compare-at for the next unit; fallback to base price
  const p = session.value?.previews?.[t.id];
  const base = unitBreakdownFromTicket(t).inc;
  const compareAt = (p?.unit_compare_at_cents_inc != null) ? p.unit_compare_at_cents_inc : base;

  // Effective (next unit) price we’re showing
  const effective = (p?.unit_effective_cents_inc != null)
    ? p.unit_effective_cents_inc
    : Math.min(base, discountedTicketPrice(t));

  return compareAt > effective ? compareAt : null;
}

const qtyInCart = (ticketId: number) => qtyByTicket.value[ticketId] ?? 0

function showDiscountCapNotice(t: Ticket) {
  if (!isPriceDiscount()) return false;

  // must be in cart
  if (qtyInCart(t.id) <= 0) return false;

  // if the next unit wouldn’t be discounted (order-level cap hit)
  if (!nextUnitIsDiscounted(t.id)) return true;

  // extra: if you support per-ticket caps from the server
  const rb = session.value?.caps?.remaining_by_ticket ?? {};
  if (Number.isInteger(rb[t.id]) && rb[t.id] === 0) return true;

  return false;
}


/* =======================
   STEP LOGIC + Stripe / Free checkout
   ======================= */
type Step = 'tickets' | 'pay' | 'success'
const step = ref<Step>('tickets')

const holdExpiresAt = ref<string | null>(null)
const secondsLeft = ref<number | null>(null)
let holdTimerId: number | null = null

function clearHoldTimer() {
  if (holdTimerId !== null) {
    window.clearInterval(holdTimerId)
    holdTimerId = null
  }
}

function startHoldTimer() {
  clearHoldTimer()

  if (!holdExpiresAt.value) {
    secondsLeft.value = null
    return
  }

  const target = dayjs(holdExpiresAt.value)
  // Calculate initial value immediately to avoid flash
  const initialDiff = target.diff(dayjs(), 'second')
  secondsLeft.value = initialDiff > 0 ? initialDiff : 0

  holdTimerId = window.setInterval(() => {
    const diff = target.diff(dayjs(), 'second')
    secondsLeft.value = diff

    if (diff <= 0) {
      clearHoldTimer()
      secondsLeft.value = 0
    }
  }, 1000)
}

const formattedTimeLeft = computed(() => {
  if (secondsLeft.value == null || secondsLeft.value < 0) return ''
  const total = secondsLeft.value
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
})

const timeLeftClass = computed(() => {
  if (secondsLeft.value == null) return 'text-slate-700'

  if (secondsLeft.value <= 120) {
    // last 2 minutes → red
    return 'text-red-600'
  }

  if (secondsLeft.value <= 600) {
    // last 10 minutes → amber
    return 'text-amber-600'
  }

  return 'text-slate-700'
})

// ✅ Back button handling: treat one "back" as going from Pay → Tickets
let pushedPayHistory = false

function pushPayHistoryState() {
  if (typeof window === 'undefined') return
  if (pushedPayHistory) return

  // Add a lightweight state marker so a back press lands here
  window.history.pushState(
    { ...(window.history.state || {}), sjWidgetStep: 'pay' },
    '',
    window.location.href
  )

  pushedPayHistory = true
}

async function handlePopState(e: PopStateEvent) {
  if (step.value !== 'pay') return

  if (session.value) {
    try {
      await releaseSessionHolds(session.value.id)
    } catch (err) {
      console.warn('Failed to release holds on popstate', err)
    }
  }

  clearHoldTimer()
  holdExpiresAt.value = null
  secondsLeft.value = null

  if (paymentElementRef.value) {
    try { paymentElementRef.value.unmount() } catch {}
    paymentElementRef.value = null
  }
  elements.value = null
  paymentReady.value = false

  step.value = 'tickets'
  pushedPayHistory = false

  await rehydrate?.('back-from-pay')

  // Keep history state sane for subsequent backs
  window.history.pushState(
    { ...(e.state || window.history.state || {}), sjWidgetStep: 'tickets' },
    '',
    window.location.href
  )
}

// Attach / detach the listener
onMounted(() => {
  window.addEventListener('popstate', handlePopState)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handlePopState)
  // keep our previous cleanup here:
  clearCartSessionStorage()
})

const stripe = ref<Stripe | null>(null)
const elements = ref<StripeElements | null>(null)
const clientSecret = ref<string | null>(null)
const paying = ref(false)
const paymentElementRef = ref<any | null>(null)
const paymentReady = ref(false)

const checkoutStatus = ref<'idle'|'free'|'requires_payment'>('idle')
const isFreeCheckout = computed(() =>
  checkoutStatus.value === 'free' ||
  ((safeTotals.value.total_cents_inc ?? 0) === 0)
)



// ✅ Subtotal including tax – prefer backend field, fall back to ex if missing
// Subtotal (inc) – prefer the backend's inc subtotal, fall back to ex if needed
const subtotalIncCents = computed(() => {
  // Use backend subtotal_inc directly
  return safeTotals.value.subtotal_cents_inc ?? 0
})



const feesIncCents = computed(() => {
  // total_inc = subtotal_inc + fees_inc  (discount is already baked into subtotal_inc)
  const total   = safeTotals.value.total_cents_inc ?? 0
  const subInc  = safeTotals.value.subtotal_cents_inc ?? 0
  if (!total || !subInc) return 0
  return Math.max(0, total - subInc)
})

function lineCompareAtInc(i: SessionItem): number | null {
  const uComp = i.unit_compare_at_cents_inc ?? null
  const uEff  = i.unit_effective_cents_inc ?? null
  const qty   = i.quantity ?? 0

  // Only show if we truly have a higher compare-at price
  if (uComp != null && uEff != null && uComp > uEff && qty > 0) {
    return uComp * qty
  }
  return null
}


// 1) Subtotal = all tickets combined, incl GST, *excluding* booking/processing fees
const ticketsSubtotalIncCents = computed(() => {
  // backend subtotal_cents_inc is the tickets (inc GST)
  return safeTotals.value.subtotal_cents_inc ?? 0
})




// 3) Label for that fee line – prefer booking_fee_label if backend sends it,
//    otherwise fall back to processing_fee_label, otherwise "Booking fee".
const bookingFeeLabel = computed(() => {
  const totals: any = session.value?.totals || {}
  return (
    totals.booking_fee_label ||
    safeTotals.value.processing_fee_label ||
    'Booking fee'
  )
})

// Is there a paid cart at all? (subtotal > 0, not including booking fee)
const hasPaidCart = computed(() => {
  // In local mode, check the subtotal directly
  if (isDirty.value && clientTotals.value) {
    return clientTotals.value.subtotal_cents_inc > 0
  }
  // Server-driven mode: check session subtotal
  return (session.value?.totals?.subtotal_cents_inc ?? 0) > 0
})

// Booking fee (INC):
// - 0 when cart is empty or free
// - booking_fee_cents_inc when there is a paid cart
const bookingFeeIncCents = computed(() => {
  // When dirty (local mode), use client-computed booking fee
  if (isDirty.value && clientTotals.value) {
    return clientTotals.value.booking_fee_cents_inc
  }
  // Server-driven mode
  if (!hasPaidCart.value) return 0
  return session.value?.totals?.booking_fee_cents_inc ?? 0
})

// Subtotal (INC) = line items total (tickets + processing fees, before booking fee)
// When a discount is applied, show the UNDISCOUNTED subtotal so users see the original price
const displaySubtotalIncCents = computed(() => {
  // In local mode (no voucher), use the subtotal directly
  if (isDirty.value && clientTotals.value) {
    return clientTotals.value.subtotal_cents_inc
  }
  
  // Server-driven mode
  const totals = session.value?.totals
  if (!totals) return 0
  
  const bookingFee = totals.booking_fee_cents_inc ?? 0
  
  // If there's an active discount, show undiscounted total minus booking fee
  // This gives us the full ticket prices (including processing fees) before discount
  if (hasActiveDiscount.value && typeof totals.undiscounted_total_cents_inc === 'number') {
    return totals.undiscounted_total_cents_inc - bookingFee
  }
  
  // No discount: total minus booking fee = sum of line items
  return (totals.total_cents_inc ?? 0) - bookingFee
})

// Booking Fee line amount in the UI
const displayFeeIncCents = computed(() => bookingFeeIncCents.value)

const isDiscountedLine = (i: SessionItem) => {
  return !!i.was_discounted || (i.line_discount_cents_ex ?? 0) > 0
}

const lineStrikeInc = (i: SessionItem): number | null => {
  if (!isDiscountedLine(i)) return null

  // Use the item's compare-at price (undiscounted unit price) times quantity
  // This should NOT include the booking fee - that's shown separately
  const unitCompare = i.unit_compare_at_cents_inc ?? null
  if (unitCompare == null) return null

  return i.quantity * unitCompare
}


const pollTimer = ref<number | null>(null)
const orderId   = ref<number | null>(null)
const orderNumber = ref<number | null>(null)
const pollAttempts = ref(0)
const maxPollAttempts = 60
const pollDelayMs = 1500

const totalQty = computed(() => {
  // When dirty (local mode), compute from local state
  if (isDirty.value) {
    return Object.values(qtyByTicket.value).reduce((sum, q) => sum + q, 0)
  }
  // Server-driven mode
  return (session.value?.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0)
})

// Generate display items for the order summary
const displayItems = computed(() => {
  // When dirty (local mode), generate items from local state
  if (isDirty.value) {
    const items: any[] = []
    
    Object.entries(qtyByTicket.value)
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([tidStr, qty]) => {
        if (qty <= 0) return
        const tid = Number(tidStr)
        const t = ticketMap[tid]
        if (!t) return

        const unitInc = unitBreakdownFromTicket(t).inc
        const lineTotal = unitInc * qty

        items.push({
          id: `local-${tid}`,
          ticket_id: tid,
          ticket_name: t.name,
          quantity: qty,
          line_total_cents_inc: lineTotal,
          unit_effective_cents_inc: unitInc,
          unit_compare_at_cents_inc: null, // No discount in local mode
          group_label: (event.value?.groups?.find(g => g.id === ticketToGroup[tid]) as any)?.label,
        })
      })
    
    return items
  }
  
  // Server-driven mode: use session items
  return session.value?.items || []
})

const isFree = computed(() => (safeTotals.value.total_cents_inc ?? 0) === 0)

async function ensureStripe() {
  if (!stripe.value) {
    const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string
    stripe.value = await loadStripe(pk)
  }
}

function backToTicketsFromTimeout() {
  const currentSessionId = session.value?.id

  // ---- 1) UI cleanup (instant) ----
  
  // Reset checkout processing state
  isProcessingCheckout.value = false
  ;(startCheckout as any)._busy = false

  clearHoldTimer()
  holdExpiresAt.value = null
  secondsLeft.value = null

  if (paymentElementRef.value) {
    try { paymentElementRef.value.unmount() } catch {}
    paymentElementRef.value = null
  }
  elements.value = null
  paymentReady.value = false
  clientSecret.value = null // Reset so new payment intent is created on next checkout

  // Leave the timeout screen and return to tickets instantly
  step.value = 'tickets'
  pushedPayHistory = false

  // ---- 2) Fire-and-forget cleanup ----

  if (currentSessionId) {
    releaseSessionHolds(currentSessionId).catch(err => {
      console.warn('Failed to release holds after timeout', err)
    })
  }

  rehydrate?.('hold-expired').catch(err => {
    console.warn('Failed rehydrate after timeout', err)
  })
}

const isProcessingCheckout = ref(false)
const isProcessingBack = ref(false)

async function goToCheckout() {
  if (!session.value || totalQty.value === 0) return

  isProcessingCheckout.value = true
  resetErrors()

  try {
    // 1) If cart is dirty (local mode), sync to backend first
    if (isDirty.value) {
      const itemsToSync = Object.entries(qtyByTicket.value)
        .map(([tid, qty]) => ({ ticket_id: Number(tid), quantity: qty }))
        .filter(i => i.quantity > 0)

      const syncRes = await syncSessionItems(session.value.id, itemsToSync)
      const syncData = (syncRes as any).data ?? syncRes
      applySessionWithNotices(syncData)
      isDirty.value = false
      clientTotals.value = null
      clearLocalCart()
      
      // Sync local qty state with what the server returned (items may have been removed/adjusted)
      hydrateLocalQtyFromSession()
      
      // If sync returned blocking notices (items removed/adjusted) or empty cart, stay on tickets
      const syncBlockingNoticeCodes = [
        'ticket_removed_unavailable',
        'ticket_removed_sold_out', 
        'ticket_removed_sales_ended',
        'ticket_quantity_reduced',
        'cart_updated_summary',
      ]
      const hasSyncBlockingNotice = syncData.notices?.some((n: any) => 
        syncBlockingNoticeCodes.includes(n.code)
      )
      
      if (hasSyncBlockingNotice || !syncData.items?.length) {
        step.value = 'tickets'
        // Refresh event data so unavailable tickets show as greyed out
        try {
          await rebuildEventView(session.value?.id)
        } catch (e) {
          console.warn('Failed to refresh event data:', e)
        }
        scrollToTicketSection()
        return
      }
    }

    // 2) Validate and place holds
    const res = await validateSessionBeforeCheckout(session.value.id)
    const updated = (res as any).data ?? res

    applySessionWithNotices(updated)
    
    // Sync local qty state with what the server returned
    hydrateLocalQtyFromSession()

    // Only block checkout for "error" notices that actually removed/changed items
    // Informational notices like "ticket_limit_reached" should NOT block checkout
    const blockingNoticeCodes = [
      'ticket_removed_unavailable',
      'ticket_removed_sold_out', 
      'ticket_removed_sales_ended',
      'ticket_quantity_reduced',
      'cart_updated_summary',
    ]
    const hasBlockingNotice = updated.notices?.some((n: any) => 
      blockingNoticeCodes.includes(n.code)
    )
    
    if (!updated.items?.length || hasBlockingNotice) {
      step.value = 'tickets'
      // Refresh event data so unavailable tickets show as greyed out
      try {
        await rebuildEventView(session.value?.id)
      } catch (e) {
        console.warn('Failed to refresh event data:', e)
      }
      scrollToTicketSection()
      return
    }

    holdExpiresAt.value = updated.meta?.hold_expires_at || null
    startHoldTimer()

    // Clear notices and reset promo input when successfully reaching checkout (if no voucher)
    clearAllNotices()
    if (!hasActiveDiscount.value) {
      code.value = ''
    }

    step.value = 'pay'
    pushPayHistoryState()

    const grandInc = updated?.totals?.total_cents_inc ?? 0

    if (grandInc <= 0) {
      checkoutStatus.value = 'free'
      // Wait for checkout step to render, then scroll
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 200))
      scrollToTicketSection()
      return
    }

    checkoutStatus.value = 'requires_payment'
    await startCheckout()
    // Wait for checkout pane to render after API returns, then scroll
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 200))
    scrollToTicketSection()
  } catch (e) {
    console.error(e)
  } finally {
    isProcessingCheckout.value = false
  }
}


// 2) startCheckout(): guard the refresh, keep user on Pay step unless 409
// 2) startCheckout(): mount / reuse Stripe Payment Element, no step changes
async function startCheckout() {
  // ==== Idempotent fast-exit: already mounted & ready ====
  if (clientSecret.value && paymentElementRef.value && elements.value && stripe.value) {
    // we assume we're already on 'pay' step
    checkoutStatus.value = 'requires_payment'
    return
  }

  // ==== Concurrency guard ====
  if ((startCheckout as any)._busy) return
  ;(startCheckout as any)._busy = true

  try {
    if (!session.value || totalQty.value === 0) return
    resetErrors()

    paying.value = false
    paymentReady.value = false

    // Clean any previous Stripe mounts
    clientSecret.value = null
    if (paymentElementRef.value) {
      try {
        paymentElementRef.value.unmount()
      } catch {}
      paymentElementRef.value = null
    }
    elements.value = null

    // --- Refresh session (optional but safe) ---
    if (typeof fetchSessionById === 'function') {
      const fresh = await fetchSessionById(session.value.id)
      const sData = (fresh as any)?.data ?? fresh
      if (sData?.items) applySession(sData)
    }

    const grandInc = session.value?.totals?.total_cents_inc ?? 0

    // FREE → no PI, no Stripe
    if (grandInc <= 0) {
      checkoutStatus.value = 'free'
      return
    }

    // PAID → create PI then mount Payment Element
    const piRes = await createPaymentIntentForSession(session.value.id)
    const data = (piRes as any)?.data ?? piRes
    const clientSecretFromApi = data?.client_secret
    const pkFromApi = data?.publishable_key

    if (!clientSecretFromApi) throw new Error('Missing client secret')

    checkoutStatus.value = 'requires_payment'
    clientSecret.value = clientSecretFromApi

    const pk = pkFromApi || (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string)
    stripe.value = await (stripe.value ?? loadStripe(pk))
    if (!stripe.value) throw new Error('Stripe failed to load')

    // Create/mount elements
    elements.value = stripe.value.elements({
      clientSecret: clientSecret.value,
      appearance: { theme: 'stripe' },
    })

    const mountTarget = document.querySelector('#payment-element')
    if (!mountTarget) throw new Error('Payment element mount target not found')

    paymentElementRef.value = elements.value.create('payment', {
      wallets: { link: 'never', applePay: 'auto', googlePay: 'auto' },
      paymentMethodOrder: ['card', 'afterpay_clearpay', 'zip'],
    })
    paymentElementRef.value.on('change', (e: any) => {
      paymentReady.value = !!e.complete
    })
    paymentElementRef.value.mount(mountTarget)
  } catch (e: any) {
    const status = e?.response?.status
    errors.general =
      e?.message || 'Something went wrong while starting checkout. Please try again.'
    paying.value = false

    if (status === 409) {
      // Cart changed on server → bounce back to tickets
      step.value = 'tickets'
    }
  } finally {
    ;(startCheckout as any)._busy = false
  }
}

/** Place order for paid carts */
async function placeOrder() {
  if (!session.value || !elements.value || !stripe.value || !clientSecret.value) return
  if (!validateBeforePlaceOrder()) {
    await nextTick()
    document.querySelector('.field-error, .form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  paying.value = true
  errors.general = ''

  // If time is running low (< 60 seconds) or already expired, extend the hold before processing payment
  if (secondsLeft.value !== null && secondsLeft.value < 60) {
    try {
      const extendRes = await extendSessionHold(session.value.id)
      const extendData = (extendRes as any)?.data ?? extendRes
      // Update the hold expiry with the extended time
      holdExpiresAt.value = extendData.meta?.hold_expires_at || null
      startHoldTimer()
    } catch (e: any) {
      // Hold expired - can't extend, show error
      paying.value = false
      errors.general = 'Your reservation has expired. Please restart checkout.'
      await nextTick()
      document.querySelector('.form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
  }

  try {
    await saveSessionConsents(session.value.id, {
      accepted_terms: !!acceptedTerms.value,
      mailing_list: !!mailingList.value,
      first_name: firstName.value.trim(),
      last_name:  lastName.value.trim(),
      email: email.value.trim(),
      full_phone: fullPhoneObject.value,
      postcode: isAuPhone.value ? postcode.value.trim() : '',
    })
  } catch (e: any) {
    paying.value = false
    const status = e?.response?.status
    const body   = e?.response?.data
    const ve     = body?.errors
    if (status === 422 && ve) {
      if (ve.first_name?.[0]) errors.first = ve.first_name[0]
      if (ve.last_name?.[0])  errors.last  = ve.last_name[0]
      if (ve.email?.[0])      errors.email = ve.email[0]
      if (ve.accepted_terms?.[0]) errors.terms = ve.accepted_terms[0]
      errors.general = body?.message || 'Please fix the highlighted fields.'
    } else if (status === 409) {
      errors.general = body?.message || 'Sorry, your cart changed. Please review your tickets and try again.'
    } else {
      errors.general = 'Could not save your details. Please try again.'
    }
    await nextTick()
    document.querySelector('.form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  try {
    const { error } = await stripe.value.confirmPayment({
      elements: elements.value,
      redirect: 'if_required',
    })
    if (error) throw error
  } catch (e: any) {
    paying.value = false
    errors.general = e?.message || 'Payment failed. Please try again.'
    await nextTick()
    document.querySelector('.form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  beginPollingForOrder()
}

/** Place order for FREE carts (no Stripe) */
async function completeFreeOrder() {
  if (!session.value) return
  if (!validateBeforePlaceOrder()) {
    await nextTick()
    document.querySelector('.field-error, .form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  paying.value = true
  errors.general = ''

  // If time is running low (< 60 seconds) or already expired, extend the hold before processing
  if (secondsLeft.value !== null && secondsLeft.value < 60) {
    try {
      const extendRes = await extendSessionHold(session.value.id)
      const extendData = (extendRes as any)?.data ?? extendRes
      holdExpiresAt.value = extendData.meta?.hold_expires_at || null
      startHoldTimer()
    } catch (e: any) {
      paying.value = false
      errors.general = 'Your reservation has expired. Please restart checkout.'
      await nextTick()
      document.querySelector('.form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
  }

  try {
    await saveSessionConsents(session.value.id, {
      accepted_terms: !!acceptedTerms.value,
      mailing_list: !!mailingList.value,
      first_name: firstName.value.trim(),
      last_name:  lastName.value.trim(),
      email: email.value.trim(),
      full_phone: fullPhoneObject.value,
      postcode: isAuPhone.value ? postcode.value.trim() : '',
    })
  } catch (e: any) {
    paying.value = false
    const status = e?.response?.status
    const body   = e?.response?.data
    const ve     = body?.errors
    if (status === 422 && ve) {
      if (ve.first_name?.[0]) errors.first = ve.first_name[0]
      if (ve.last_name?.[0])  errors.last  = ve.last_name[0]
      if (ve.email?.[0])      errors.email = ve.email[0]
      if (ve.accepted_terms?.[0]) errors.terms = ve.accepted_terms[0]
      errors.general = body?.message || 'Please fix the highlighted fields.'
    } else if (status === 409) {
      errors.general = body?.message || 'Sorry, your cart changed. Please review your tickets and try again.'
    } else {
      errors.general = 'Could not save your details. Please try again.'
    }
    await nextTick()
    document.querySelector('.form-general-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  // No PI to confirm; ask backend to finalize the free order
  beginPollingForOrder()
}

function beginPollingForOrder() {
  if (!session.value) return
  pollAttempts.value = 0
  orderId.value = null
  orderNumber.value = null

  if (pollTimer.value) { clearInterval(pollTimer.value); pollTimer.value = null }

  const isSuccessStatus = (s: string | undefined | null) =>
    s === 'ok' || s === 'completed' || s === 'success' || s === 'done'

  const tick = async () => {
    if (!session.value) return
    try {
      const res = await confirmSessionPayment(session.value.id)
      const data = (res as any)?.data ?? res
      // --- store ticket groups from confirm response ---
      if (data?.ticket_groups?.length) {
        session.value.ticket_groups = data.ticket_groups
      }      
      const status = data?.status
      const onum   = data?.order_number ?? data?.order?.order_number
      const oid    = data?.order_id     ?? data?.order?.id
      const hasPdf = !!(data?.pdf_ready || data?.pdf_url)

      if (isSuccessStatus(status) || hasPdf) {
        orderNumber.value = onum != null ? Number(onum) : null
        orderId.value     = oid  != null ? Number(oid)  : null

        if (paymentElementRef.value) {
          try { paymentElementRef.value.unmount() } catch {}
          paymentElementRef.value = null
        }
        elements.value = null

        clearInterval(pollTimer.value!)
        pollTimer.value = null
        paying.value = false
        step.value = 'success'
        clearCartSessionStorage()

        return
      }
      if (status === 'pending') { /* keep polling */ }
    } catch (e: any) {
      const status = e?.response?.status
      if (status === 202 || status === 422) {
        // keep polling
      } else if (status === 409) {
        clearInterval(pollTimer.value!)
        pollTimer.value = null
        paying.value = false
        errors.general = e?.response?.data?.message || 'Your cart changed during checkout. Please review tickets.'
        step.value = 'tickets'
        return
      } else {
        // transient: keep polling
      }
    }

    pollAttempts.value++
    if (pollAttempts.value >= maxPollAttempts) {
      clearInterval(pollTimer.value!)
      pollTimer.value = null
      paying.value = false
      errors.general = 'We’re still confirming your order. You can wait a moment and try “Place order” again.'
    }
  }

  pollTimer.value = window.setInterval(tick, pollDelayMs)
  tick()
}

onBeforeUnmount(() => { if (pollTimer.value) clearInterval(pollTimer.value) })
onBeforeUnmount(() => {
  // When the widget is closed or navigated away from, forget the cart session
  clearCartSessionStorage()
})


async function backToTickets() {
  // Reset checkout processing state immediately
  isProcessingCheckout.value = false
  
  // Reset startCheckout busy flag so next checkout attempt isn't blocked
  ;(startCheckout as any)._busy = false
  
  // 1) Clean up UI immediately

  if (paymentElementRef.value) {
    try { paymentElementRef.value.unmount() } catch {}
    paymentElementRef.value = null
  }
  elements.value = null
  paymentReady.value = false
  clientSecret.value = null // Reset so new payment intent is created on next checkout

  clearHoldTimer()
  holdExpiresAt.value = null
  secondsLeft.value = null

  const currentSessionId = session.value?.id

  // Switch back straight away – feels instant to the user
  step.value = 'tickets'
  pushedPayHistory = false
  
  // Scroll to top after step change
  await nextTick()
  scrollToTicketSection()

  // 2) Fire-and-forget: clear holds + rehydrate in background

  if (currentSessionId) {
    releaseSessionHolds(currentSessionId).catch(err => {
      console.warn('Failed to release holds on backToTickets', err)
    })
  }

  rehydrate('back-from-pay').catch(err => {
    console.warn('Failed to rehydrate after backToTickets', err)
  })
}

/* equalize qty col widths */
const qtyColWidth = ref<number>(0)
function adjustQtyWidths() {
  nextTick(() => {
    const els = document.querySelectorAll<HTMLElement>('.qty-col')
    let max = 0
    els.forEach(el => { max = Math.max(max, el.offsetWidth) })
    els.forEach(el => { el.style.minWidth = max + 'px' })
    qtyColWidth.value = max
  })
}
watch(() => session.value?.items, adjustQtyWidths, { deep: true })
onMounted(adjustQtyWidths)

/* Header height sync */
let headerRO: ResizeObserver | null = null
function setHeaderSpacer(h: number) {
  document.querySelectorAll<HTMLElement>('.js-header-height').forEach(el => { el.style.height = `${Math.round(h)}px` })
}
function syncHeaderHeight() {
  const header = document.querySelector<HTMLElement>('.js-header-event-details')
  if (!header) return
  const h = header.getBoundingClientRect().height
  setHeaderSpacer(h)
}

function syncContentArea() {
  const area = document.querySelector<HTMLElement>('.js-ticket-content-area')
  if (!area) return

  const isMobile = window.innerWidth <= 1023

  if (isMobile) {
    const headerH = getHeaderHeight()
    const value = `calc(100svh - ${headerH}px)`

    area.style.setProperty('height', value, 'important')
    area.style.setProperty('overflow-y', 'auto')
    ;(area.style as any).WebkitOverflowScrolling = 'touch'
    ;(area.style as any).overscrollBehavior = 'contain'
  } else {
    // Reset styles on desktop
    area.style.removeProperty('height')
    area.style.removeProperty('overflow-y')
    area.style.removeProperty('-webkit-overflow-scrolling')
    area.style.removeProperty('overscroll-behavior')
  }
}

onMounted(() => {
  nextTick(() => {
    syncHeaderHeight()
    syncSummaryHeight()
    syncContentArea()            // ✅ NEW
  })

  window.addEventListener('resize', () => {
    syncHeaderHeight()
    syncSummaryHeight()
    syncContentArea()            // ✅ NEW
  }, { passive: true })

  const header = document.querySelector<HTMLElement>('.js-header-event-details')
  if ('ResizeObserver' in window && header) {
    headerRO = new ResizeObserver(() => {
      syncHeaderHeight()
      syncSummaryHeight()
      syncContentArea()          // ✅ NEW
    })
    headerRO.observe(header)
  }

  window.addEventListener('load', () => {
    syncHeaderHeight()
    syncSummaryHeight()
    syncContentArea()            // ✅ NEW
  })
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', syncHeaderHeight)
  window.removeEventListener('load', syncHeaderHeight)
  headerRO?.disconnect()
  headerRO = null
})

const hostH = ref<number>(0);            

// ===== Viewport + debounce helpers =====
const supportsDVH = CSS.supports?.('height', '100dvh');

// ===== Rehydrate debounce =====
function rafDebounce<T extends any[]>(fn: (...args: T) => void, delay = 80) {
  let raf: number | null = null;
  let t: number | null = null;
  return (...args: T) => {
    if (raf) cancelAnimationFrame(raf);
    if (t) clearTimeout(t);
    raf = requestAnimationFrame(() => {
      t = window.setTimeout(() => {
        fn(...args);
        raf = null;
        t = null;
      }, delay);
    });
  };
}

function getHeaderHeight(): number {
  const el = document.querySelector<HTMLElement>('.js-header-event-details');
  return el ? Math.round(el.getBoundingClientRect().height) : 0;
}

function computeHostHeight(): number {
  const headerH = getHeaderHeight();
  if (supportsDVH && window.visualViewport?.height) {
    return Math.max(0, Math.round(window.visualViewport.height - headerH));
  }
  return Math.max(0, Math.round(window.innerHeight - headerH));
}

/* Right column height sync */
let summaryRO: ResizeObserver | null = null
let hostRO: ResizeObserver | null = null
function getNearestScrollContainer(el: HTMLElement | null): HTMLElement | null {
  let p = el?.parentElement
  while (p && p !== document.body) {
    const oy = getComputedStyle(p).overflowY
    if (/(auto|scroll|overlay)/.test(oy)) return p
    p = p.parentElement
  }
  return null
}
function getVisibleHostHeight(): number {
  const iframeViewport = document.documentElement.clientHeight
  const root = document.querySelector<HTMLElement>('.ticket-widget')
  const scrollHost = getNearestScrollContainer(root || null)
  if (scrollHost) return Math.round(scrollHost.clientHeight)
  return Math.round(iframeViewport)
}
const syncSummaryHeight = () => {
  const wrapper = document.querySelector<HTMLElement>('.js-ticket-right-wrapper');
  if (!wrapper) return;

  const isMobile = window.innerWidth <= 767;

  if (isMobile) {
    const headerH = getHeaderHeight();

    if (supportsDVH) {
      // --- Mobile: use dynamic viewport height (for Safari/iOS, Chrome) ---
      wrapper.style.setProperty('--headerH', `${headerH}px`);
      wrapper.style.removeProperty('height'); // let CSS use dvh + var(--headerH)
    } else {
      // --- Fallback: calc(100vh − header height) ---
      const value = `calc(100vh - ${headerH}px)`;
      wrapper.style.setProperty('height', value, 'important');
      wrapper.style.removeProperty('--headerH');
    }

    return;
  }

  // --- Desktop: original desktop behavior ---
  const h = getVisibleHostHeight();
  wrapper.style.removeProperty('--headerH');
  wrapper.style.removeProperty('height'); // avoid leaking mobile height to desktop
  wrapper.style.height = `${h}px`;
};
async function runLayoutPass(reason = 'layout') {
  await nextTick()
  syncSummaryHeight()
  // If you size any other areas (middle content, etc.), call their sync here too.
  syncContentArea();
}

onMounted(() => {
  nextTick(syncSummaryHeight)
  window.addEventListener('resize', syncSummaryHeight, { passive: true })
  const root = document.querySelector<HTMLElement>('.ticket-widget')
  const host = getNearestScrollContainer(root || null)
  if ('ResizeObserver' in window && host) {
    hostRO = new ResizeObserver(syncSummaryHeight)
    hostRO.observe(host)
  }
  window.addEventListener('load', syncSummaryHeight)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', syncSummaryHeight)
  window.removeEventListener('load', syncSummaryHeight)
  summaryRO?.disconnect()
  hostRO?.disconnect()
  summaryRO = hostRO = null
})

/* Sales-end badges */
const now = ref(dayjs())
let nowTimer: number | null = null
onMounted(() => { nowTimer = window.setInterval(() => (now.value = dayjs()), 1000) })
onBeforeUnmount(() => { if (nowTimer) clearInterval(nowTimer) })

const USE_COUNTDOWN = false
function formatEndAbsolute(endIso: string) {
  const zone = dayjs.tz.guess()
  const now  = dayjs().tz(zone)
  const end  = dayjs.utc(endIso).tz(zone)
  const isToday = end.isSame(now, 'day')
  const time    = end.format(end.minute() === 0 ? 'ha' : 'h:mma')
  return isToday
    ? `Sales end today at ${time}`
    : `Sales end on ${end.format('MMM D')} at ${time}`
}
function formatCountdown(endIso: string) {
  if (!USE_COUNTDOWN) return formatEndAbsolute(endIso)
  const zone = dayjs.tz.guess()
  const now  = dayjs().tz(zone)
  const end  = dayjs.utc(endIso).tz(zone)
  const diff = end.diff(now, 'second')
  if (diff <= 0) return 'Sales ended'
  const h = Math.floor(diff / 3600)
  const m = Math.floor((diff % 3600) / 60)
  const s = diff % 60
  if (h > 0) return `Sales end in ${h}h ${m}m ${s}s`
  if (m > 0) return `Sales end in ${m}m ${s}s`
  return `Sales end in ${s}s`
}

// your existing "sales end" logic but made safe:
const showSalesEndBadgeSafe = (t: Ticket) =>
  showSalesEndBadge(t) && !isDiscountContextForTicket(t) && !showUnlockedBadge(t)

function showSalesEndBadge(t: Ticket) {
  return !!(t as any).show_sales_end_date && !!(t as any).sales_window?.end && !(t as any).sales_ended
}

// Keep your old formatting but wrap it for a single call-site
const salesEndLabel = (t: Ticket) => salesEndBadgeText(t, event?.timezone)

function salesEndBadgeText(t: Ticket) {
  const endIso = (t as any).sales_window?.end
  if (!endIso) return null
  return USE_COUNTDOWN ? formatCountdown(endIso) : formatEndAbsolute(endIso)
}

// Single source of truth for badge text (priority)
const badgeTextFor = (t: Ticket): string | null => {
  if (showSoldOutBadge(t)) return 'Sold out'
  if (isDiscountContextForTicket(t)) return 'Discount applied'
  if (showUnlockedBadge(t)) return 'Unlocked'
  if (showSalesEndBadgeSafe(t)) return salesEndLabel(t)
  return null
}


/* Mobile summary drawer */
const cartExpanded = ref(false)
const hostExpanded = ref(false)   // drives the host’s .is-expanded class
const isCartAnimating = ref(false)
const isOpening = ref(false)
const ANIM_MS = 320

function toggleCart() {
  const opening = !cartExpanded.value
  isOpening.value = opening
  isCartAnimating.value = true

  if (opening) {
    // OPEN: add host flag first so it can grow beyond 102px
    hostExpanded.value = true
    cartExpanded.value = true
    window.setTimeout(() => {
      isCartAnimating.value = false
    }, ANIM_MS)
  } else {
    // CLOSE: start collapsing content, but keep host expanded
    cartExpanded.value = false
    window.setTimeout(() => {
      // after the collapse animation finishes, remove host expansion
      hostExpanded.value = false
      isCartAnimating.value = false
    }, ANIM_MS)
  }
}

// add near other refs
// Remember mobile expand state when leaving tickets
const prevMobileExpanded = ref(false)

const summaryHostClass = computed(() => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1023
  return {
    'is-collapsed': isMobile && step.value !== 'tickets',
    'is-expanded':  isMobile && step.value === 'tickets' && hostExpanded.value,
    'no-transition': isCartAnimating.value && isOpening.value, // only while opening
  }
})


// When step changes, collapse on mobile, and restore old height when returning
watch(step, async (val, oldVal) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1023

  if (isMobile) {
    if (val !== 'tickets') {
      // leaving tickets → capture current mobile drawer state, then close it
      prevMobileExpanded.value = cartExpanded.value
      cartExpanded.value = false
    } else {
      // returning to tickets → restore the previous state
      cartExpanded.value = prevMobileExpanded.value
    }
  }

  await nextTick()
  try { syncSummaryHeight() } catch {}
})

// keep your computed with the collapse class
const summaryWrapperClass = computed(() => ({
  'ticket-widget__summary--wrapper': true,
  'js-ticket-right-wrapper': true,
  'is-expanded': cartExpanded.value,
}))

// helper to derive the subline for a group
const activeGroupSubline = computed(() => {
  const g: any = activeGroup.value
  if (g.is_full_span || g.full_span) {
    return (
      g.display?.date_range ||          // per-group date range if you have it
      event.value?.display?.date_range || '' // fallback to event-level date range
    )
  }
  const schedule = g.display?.schedule                       // e.g., "1:30pm – 8:30pm"
      || g.display?.bottom_time                          // e.g., "10 Sep 2025 · 1:30pm – 8:30pm"
      || g.day_rows?.[0]?.time_label
      || ''
  
  // Replace middle dot (·) with comma
  return schedule.replace(/ · /g, ', ')
})

// --- Embed/host detection + actions ---
const isInIframe = () => {
  try { return window.self !== window.top } catch { return true }  // cross-origin safe
};


// When used as a modal on your landing page (not iframe), the host page
// should listen for this event and close the popup.
function requestHostClose() {
  window.dispatchEvent(new CustomEvent('sj:close-widget'));
}

// Open the event page appropriately:
// • In iframe → open new tab
// • In landing-page modal → ask host to close, then soft-navigate (fallback to hard nav)
function goBackToEvent() {
  const slugSafe = event.value?.slug;
  if (!slugSafe) return;
  const url = `/event/${slugSafe}`;

  if (isInIframe()) {
    window.open(url, '_blank', 'noopener');
    return;
  }

  // Ask host (if any) to close the modal
  requestHostClose();

  // If this widget is the full page itself, just navigate
  try {
    router.push(url);
  } catch {
    window.location.href = url;
  }
}

// Single-group detection for "View tickets" CTA on success page
const singleGroupPublicId = computed<string | null>(() => {
  const groups: any[] = (event.value?.groups ?? []);
  if (groups.length !== 1) return null;

  const g = groups[0];
  // Prefer a public identifier if you have one; otherwise fallback
  const pub =
    g.public_id ??
    g.uuid ??
    g.slug ??
    g.id; // only use numeric id if your route accepts it

  return pub ? String(pub) : null;
});

// Build the final tickets URL only when allowed
const singleGroupTicketsUrl = computed<string | null>(() =>
  singleGroupPublicId.value ? `/tickets/group/${singleGroupPublicId.value}` : null
);

// Extract ticket groups (if confirm returned them)
const ticketGroups = computed(() => session.value?.ticket_groups ?? [])

// Only show View Tickets if exactly one group
const singleGroup = computed(() =>
  ticketGroups.value.length === 1 ? ticketGroups.value[0] : null
)

// Build the handler for the button
function openTicketsLink() {
  const href = singleGroup.value?.url
  if (!href) return
  window.open(href, '_blank', 'noopener')
}

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVis)
  window.removeEventListener('pageshow', onPageShow)
  window.removeEventListener('focus', onFocus)
})

defineExpose({ rehydrate })

watch(step, async (newStep, oldStep) => {
  await nextTick(); // wait for DOM to update
  // Use the same scroll method as notices
  scrollToTicketSection();
});

</script>

<template>

  <div class="relative">
    <!-- Boot overlay -->
    <Transition name="fade">
      <div
        v-if="isBooting"
        class="absolute lg:h-[90vh] inset-0 z-[100000] grid place-items-center bg-white"
      >
          <LoadingIcon icon="tail-spin" color="#000000" class="w-8 h-8" />

      </div>
    </Transition>    
    <div class="mx-auto max-w-6xl ticket-widget">
      <div class="grid grid-cols-1 md:grid-cols-3">
        <!-- LEFT: stepper -->
        <div class="md:col-span-2">
          <div class="ticket-widget__header js-header-event-details ticket-widget--padding">
            <div class="hidden md:block ticket-widget__header__logo mb-3">
              <img class='w-[53px]' src="/src/assets/images/logos/sj-logo-2025.svg"/>
            </div>
            <h1 class="text-xl md:text-3xl font-semibold mb-3">{{ event?.name }}</h1>

            <div class="ticket-widget__header__icons flex flex-col gap-3">
              <div class="flex gap-2">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.75002 1C4.75002 0.801088 4.671 0.610322 4.53035 0.46967C4.3897 0.329018 4.19893 0.25 4.00002 0.25C3.80111 0.25 3.61034 0.329018 3.46969 0.46967C3.32904 0.610322 3.25002 0.801088 3.25002 1V2.816C2.52048 2.91945 1.84483 3.25882 1.32625 3.78228C0.807669 4.30573 0.474642 4.98452 0.378019 5.715L0.291019 6.368C0.276352 6.478 0.262353 6.58867 0.249019 6.7C0.240954 6.76928 0.247681 6.83948 0.268756 6.90597C0.28983 6.97245 0.324772 7.03371 0.371273 7.0857C0.417775 7.13768 0.474777 7.1792 0.538514 7.20753C0.602251 7.23585 0.671273 7.25033 0.741019 7.25H17.26C17.3298 7.25033 17.3988 7.23585 17.4625 7.20753C17.5263 7.1792 17.5833 7.13768 17.6298 7.0857C17.6763 7.03371 17.7112 6.97245 17.7323 6.90597C17.7534 6.83948 17.7601 6.76928 17.752 6.7L17.71 6.368L17.623 5.715C17.5264 4.98452 17.1934 4.30573 16.6748 3.78228C16.1562 3.25882 15.4806 2.91945 14.751 2.816V1C14.751 0.801088 14.672 0.610322 14.5313 0.46967C14.3907 0.329018 14.1999 0.25 14.001 0.25C13.8021 0.25 13.6113 0.329018 13.4707 0.46967C13.33 0.610322 13.251 0.801088 13.251 1V2.668C10.4233 2.41664 7.57878 2.41664 4.75102 2.668L4.75002 1ZM17.945 9.226C17.9403 9.09778 17.886 8.97641 17.7934 8.88757C17.7008 8.79873 17.5773 8.7494 17.449 8.75H0.551019C0.422717 8.7494 0.299212 8.79873 0.20664 8.88757C0.114069 8.97641 0.0596928 9.09778 0.0550192 9.226C-0.00411167 11.0357 0.106371 12.8469 0.385019 14.636C0.487719 15.2951 0.80647 15.9015 1.2912 16.3598C1.77593 16.8181 2.39917 17.1024 3.06302 17.168L4.25602 17.286C7.41102 17.596 10.589 17.596 13.744 17.286L14.937 17.168C15.6009 17.1024 16.2241 16.8181 16.7088 16.3598C17.1936 15.9015 17.5123 15.2951 17.615 14.636C17.8937 12.8469 18.0042 11.0357 17.945 9.226Z" fill="white"/>
                </svg>
                <div>
                  {{ dateLine }}
                </div>
              </div>
              <div class="flex gap-2">
                <svg width="16" height="21" viewBox="0 0 16 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7.262 20.134C7.262 20.134 0 14.018 0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8C16 14.018 8.738 20.134 8.738 20.134C8.334 20.506 7.669 20.502 7.262 20.134ZM8 11.5C8.45963 11.5 8.91475 11.4095 9.33939 11.2336C9.76403 11.0577 10.1499 10.7999 10.4749 10.4749C10.7999 10.1499 11.0577 9.76403 11.2336 9.33939C11.4095 8.91475 11.5 8.45963 11.5 8C11.5 7.54037 11.4095 7.08525 11.2336 6.66061C11.0577 6.23597 10.7999 5.85013 10.4749 5.52513C10.1499 5.20012 9.76403 4.94231 9.33939 4.76642C8.91475 4.59053 8.45963 4.5 8 4.5C7.07174 4.5 6.1815 4.86875 5.52513 5.52513C4.86875 6.1815 4.5 7.07174 4.5 8C4.5 8.92826 4.86875 9.8185 5.52513 10.4749C6.1815 11.1313 7.07174 11.5 8 11.5Z" fill="white"/>
                </svg>
                <div>
                  <a v-if="addressHref" :href="addressHref" target="_blank" rel="noopener">
                    {{ addressText }}
                  </a>
                  <span v-else>{{ addressText || 'Address TBC' }}</span>
                </div>            
              </div>
            </div>

          </div>


          <div class="ticket-widget--padding ticket-content-area js-ticket-content-area">
            <!-- Height-locked host for smooth whoosh transitions -->
            <div
              ref="stepHost"
              class="relative overflow-hidden"
            >
              <Transition
                name="slide-x"
                mode="out-in"
              >
                <!-- Step 1: Tickets -->
                <div v-if="step === 'tickets'" key="tickets" class="step-pane tickets-step-1">
                  <!-- Tabs -->
                   <div v-if="hasAvailableTickets">
                    <div
                      v-if="hasGroupTabs"
                      class="mt-2 mb-3 flex flex-wrap gap-2 ticket-groups"
                      role="tablist"
                    >
                      <button
                        v-for="g in visibleGroupsSorted"
                        :key="g.id"
                        role="tab"
                        :aria-selected="(g.id === currentGroupId).toString()"
                        @click="pickGroup(g.id)"
                        class="ticket-group px-3 py-1 border rounded-md transition text-center"
                        :class="{ 'ticket-group--selected': g.id === currentGroupId }"
                      >
                        {{ g.label || g.display?.date_range || '—' }}
                      </button>
                    </div>

                    <div v-if="activeGroup" class="mb-4 pb-2 border-b border-slate-200">
                      <div class="text-lg font-bold text-slate-900">
                        <span>{{ activeGroup.label || 'Event' }}</span>
                        <span v-if="activeGroupSubline" class="ml-2 text-[13px] font-normal text-slate-600">
                          {{ activeGroupSubline }}
                        </span>
                      </div>
                    </div>

                    <div v-if="notices.length" class="mb-4 space-y-2 ticket-widget__notices">
                      <div
                        v-for="(n, idx) in notices"
                        :key="n.code + '-' + (n.ticket_id ?? '') + '-' + idx"
                        class="rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-800 flex items-start justify-between gap-2"
                      >
                        <span>{{ n.message }}</span>
                        <button
                          type="button"
                          class="text-amber-600 hover:text-amber-800 flex-shrink-0 p-0.5"
                          @click="dismissNotice(idx)"
                          aria-label="Dismiss notice"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>                    

                    <!-- Promo -->
                    <div>
                      <div class="flex gap-3 items-center">
                        <div class="promo-code-button--wrapper">
                          <input
                            :value="hasActiveDiscount ? appliedCode : code"
                            :readonly="hasActiveDiscount"
                            @input="e => code = (e.target as HTMLInputElement).value"
                            type="text"
                            placeholder="Promo Code"
                            class="promo-code-button__input w-full rounded border border-slate-300 px-3 py-2 outline-none"
                          />

                          <button
                            v-if="!hasActiveDiscount"
                            class="promo-code-button__button px-4 py-2 rounded bg-slate-900 text-white disabled:opacity-50"
                            :disabled="!code || applyingCode || !session"
                            @click="applyCode"
                          >
                            {{ applyingCode ? 'Applying…' : 'Apply' }}
                          </button>

                          <button
                            v-else
                            class="promo-code-button__button px-4 py-2 rounded border border-slate-300 text-slate-700 disabled:opacity-50"
                            :disabled="applyingCode"
                            @click="removeCode"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div class="mt-2 mb-2 text-sm">
                        <span v-if="promoError" class="text-red-600">{{ promoError }}</span>
                      </div>
                    </div>
                 

                    <!-- Tickets -->
                    <div class="space-y-4" v-if="activeGroup">
                      <div
                        v-for="(t, idx) in sortedTicketsForActiveGroup"
                        :key="t.id"
                        class="ticket-card rounded-lg p-4 relative"
                        :class="{
                          'mt-6': idx === 0 && (
                            showUnlockedBadge(t)
                            || isDiscountContextForTicket(t)
                            || isNormalLimitReached(t)
                            || showSalesEndBadge(t)
                            || showSoldOutBadge(t)
                          ),
                          'ticket-card--disabled': !isEffectivelySellable(t) && !showUnlockedBadge(t),

                          /* 🔴 red only when NOT discounted/revealed */
                          'ticket-card--ending': showSalesEndBadge(t) && !isDiscountContextForTicket(t) && !showUnlockedBadge(t),

                          /* 🟢 keep green for amount/percent discounts even if cap reached */
                          'ticket-card--discounted': isDiscountContextForTicket(t),

                          /* 🟢 reveal stays green based on your existing rule */
                          'ticket-card--revealed': showUnlockedBadge(t),

                          /* 🔴 red for sold out (user consumed all stock) - only when NOT discounted/revealed */
                          'ticket-card--soldout': showSoldOutBadge(t) && !isDiscountContextForTicket(t) && !showUnlockedBadge(t),
                        }"
                      >
                        <!-- === BADGES === -->
                        <div v-if="badgeTextFor(t)" :class="['ticket-card__badge', { 'ticket-card__badge--soldout': showSoldOutBadge(t) && !isDiscountContextForTicket(t) && !showUnlockedBadge(t) }]">
                          {{ badgeTextFor(t) }}
                        </div>

                        <!-- === HEADER === -->
                        <div class="ticket-card__header flex justify-between gap-4">
                          <div class="ticket-card__header__ticket pb-3 pr-4">
                            <div class="ticket-card__header__ticket__label mb-2">Ticket</div>
                            <div class="ticket-card__header__ticket__item">{{ t.name }}</div>
                            <div class="ticket-card__header__price mt-1 mobile-only">
                              <div class="ticket-card__header__ticket__item">
                                <span class="text-slate-800">
                                  ${{ cents(priceIncFor(t)) }}
                                </span>
                                <s v-if="strikeIncFor(t) != null" class="ml-2 text-slate-400">
                                  ${{ cents(strikeIncFor(t)!) }}
                                </s>
                              </div>
                            </div>                                                    
                            <div
                              v-if="hasGroupTabs === false
                                    && displayGroups.length > 1
                                    && !isFullSpanGroupByTicket(t.id)"
                              class="text-xs text-slate-500"
                            >
                              {{ (event?.groups || []).find(g => g.id === (ticketToGroup[t.id] || -1))?.name }}
                            </div>
                            
                          </div>

                        <div class="ticket-card__header__price pb-3 pl-4 desktop-only">
                          <div class="ticket-card__header__ticket__label mb-2">Price</div>
                          <div class="ticket-card__header__ticket__item">
                            <span class="text-slate-800">
                              ${{ cents(priceIncFor(t)) }}
                            </span>
                            <s v-if="strikeIncFor(t) !== null" class="ml-2 text-slate-400">
                              ${{ cents(strikeIncFor(t)!) }}
                            </s>
                          </div>
                        </div>
                          <div class="ticket-card__header__qty flex justify-end pb-3">
                            <template v-if="isEffectivelySellable(t)">
                              <div>
                                <div class="ticket-card__header__ticket__label mb-2 opacity-0">Qty</div>
                                <div class="w-fit flex gap-3 items-center">
                                  <button
                                    class="ticket-card__header__qty__btn ticket-card__header__qty__btn--decrease bg-slate-100 disabled:opacity-50"
                                    :disabled="pendingByTicket[t.id] || (qtyByTicket[t.id] ?? 0) === 0"
                                    @click="decrement(t)"
                                    aria-label="Decrease"
                                  >
                                    <span>–</span>
                                  </button>

                                  <span class="inline-flex w-6 justify-center tabular-nums select-none ticket-card__header__ticket__item">
                                    {{ qtyByTicket[t.id] ?? 0 }}
                                  </span>

                                  <button
                                    class="ticket-card__header__qty__btn bg-black text-white disabled:opacity-50"
                                    :disabled="
                                      pendingByTicket[t.id]
                                      || isInventorySoldOut(t)
                                      || isPerTicketOrderLimitReached(t)
                                      || (isRevealContext() && (isRevealSoldOut(t) || revealOrderLimitReachedFor(t)))
                                      || !canIncrement(t)
                                    "
                                    @click="increment(t)"
                                    aria-label="Increase"
                                  >
                                    <span>+</span>
                                  </button>
                                </div>
                              </div>
                            </template>

                            <template v-else>
                              <div class="flex items-center h-full">
                                <span class="ticket-card__header__ticket__item text-slate-500 font-semibold">
                                  {{ ticketStatus(t) }}
                                </span>
                              </div>
                            </template>
                          </div>
                        </div>

                        <!-- === DESCRIPTION === -->
                        <div class="ticket-card__description pt-4">
                          <div class="min-w-0">
                            <!-- Ticket-specific notice (e.g., stock warning) - only when in cart -->
                            <div
                              v-if="ticketNotice(t.id) && qtyInCart(t.id) > 0"
                              class="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2"
                            >
                              {{ ticketNotice(t.id) }}
                            </div>
                            <!-- Discount limit reached notice - show on ALL eligible tickets -->
                            <div
                              v-else-if="isDiscountLimitReachedForTicket(t)"
                              class="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2"
                            >
                              Discount limit reached. Further tickets will be full price.
                            </div>
                            <div v-if="t.description">
                              <div
                                class="prose prose-sm max-w-none text-slate-700"
                                :class="shouldClamp(t.description) && !expanded[t.id] ? 'line-clamp' : ''"
                                v-html="t.description"
                              />
                              <button
                                v-if="shouldClamp(t.description) && !t.sales_ended && !t.sold_out && !isInventorySoldOut(t)"
                                type="button"
                                class="mt-2 text-xs font-medium underline"
                                @click="toggleExpand(t.id)"
                              >
                                {{ expanded[t.id] ? 'Show less' : 'Read more' }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                   </div>
                    <div v-else>
                      <div class="">
                        No tickets available for this event. Check back later.
                      </div>
                    </div>
                </div>

                <!-- Step 2: Pay -->
                <div v-else-if="step === 'pay'" key="pay" class="step-pane"> 
                  <div class="flex justify-between">
                    <button
                      class="back-button text-sm text-indigo-600 hover:underline inline-flex items-center justify-center px-4"
                      @click="backToTickets"
                    >
                      Back
                    </button>                    
                    <!-- Timer (always visible, no opacity transition) -->
                    <div 
                      class="text-xs mb-4"
                      :class="secondsLeft != null && secondsLeft > 0 ? '' : 'invisible'"
                    >
                      <span class="font-medium">Time remaining: </span>
                      <span :class="timeLeftClass">
                        {{ formattedTimeLeft || '0:00' }}
                      </span>
                    </div>     
                  </div>                                   
                  <!-- Event summary pill -->
                  <div class="rounded-lg border border-slate-200 p-4 mb-4 flex items-center gap-4">
                    <img v-if="heroUrl" :src="heroUrl" :alt="heroAlt" class="w-20 h-14 object-cover rounded-md hidden sm:block" referrerpolicy="no-referrer" />
                    <div class="min-w-0">
                      <div class="font-medium text-slate-900 truncate">{{ event?.name }}</div>
                      <div class="text-xs text-slate-500">{{ dateLine }}</div>
                    </div>
                    <div class="ml-auto font-semibold">AU${{ cents(safeTotals.total_cents_inc) }}</div>
                  </div>





                  
                  <!-- If time is up AND not currently paying: show timeout screen -->
                  <div
                    v-if="secondsLeft !== null && secondsLeft <= 0 && !paying"
                    class="w-full flex items-center justify-center py-12 px-4"
                  >
                    <div
                      class="bg-white rounded-2xl py-12 px-8 shadow-sm text-center max-w-xl w-full"
                    >
                      <h2 class="text-2xl font-semibold text-slate-700 mb-3">
                        Time <span class="bg-slate-100 px-1 rounded">Limit</span> Reached
                      </h2>

                      <p class="text-sm text-slate-500 leading-relaxed mb-8">
                        Your reservation has been released. Please re-start your purchase.
                      </p>

                      <button
                        class="btn-ticketing-red text-white inline-flex items-center justify-center h-10 px-4 rounded font-medium"
                        @click="backToTicketsFromTimeout"
                      >
                        Back to tickets
                      </button>   
                    </div>
                  </div>
                  

                  <!-- Otherwise: normal Pay content -->
                  <div v-else>
                    <!-- Billing information -->
                    <div class="rounded-lg border border-slate-200 p-4 mb-4">
                      <div class="flex items-center justify-between mb-3">
                        <h2 class="text-lg font-semibold">Billing information</h2>
                      </div>

                      <!-- General error -->
                      <p v-if="errors.general" class="form-general-error mb-3 text-sm text-red-600" aria-live="polite">
                        {{ errors.general }}
                      </p>

                      <div class="grid grid-cols-1 sm-grid-cols-2 sm:grid-cols-2 gap-3">
                        <div>
                          <label class="block text-xs text-slate-600 mb-1">First name *</label>
                          <input
                            v-model="firstName"
                            type="text"
                            class="w-full rounded border px-3 py-2 outline-none"
                            :class="errors.first ? 'border-red-500 field-error' : 'border-slate-300 focus:border-indigo-500'"
                            :aria-invalid="!!errors.first"
                            @blur="onFirstNameBlur"
                          />
                          <p v-if="errors.first" class="mt-1 text-xs text-red-600">{{ errors.first }}</p>
                        </div>

                        <div>
                          <label class="block text-xs text-slate-600 mb-1">Last name *</label>
                          <input
                            v-model="lastName"
                            type="text"
                            class="w-full rounded border px-3 py-2 outline-none"
                            :class="errors.last ? 'border-red-500 field-error' : 'border-slate-300 focus:border-indigo-500'"
                            :aria-invalid="!!errors.last"
                            @blur="onLastNameBlur"
                          />
                          <p v-if="errors.last" class="mt-1 text-xs text-red-600">{{ errors.last }}</p>
                        </div>

                        <div class="">
                          <label class="block text-xs text-slate-600 mb-1">Email address *</label>
                          <input
                            v-model="email"
                            type="email"
                            class="w-full rounded border px-3 py-2 outline-none"
                            :class="errors.email ? 'border-red-500 field-error' : 'border-slate-300 focus:border-indigo-500'"
                            :aria-invalid="!!errors.email"
                            @blur="onEmailBlur"
                          />
                          <p v-if="errors.email" class="mt-1 text-xs text-red-600">{{ errors.email }}</p>
                        </div>

                        <div class="">
                          <label class="block text-xs text-slate-600 mb-1">Confirm email address *</label>
                          <input
                            v-model="confirmEmail"
                            type="email"
                            class="w-full rounded border px-3 py-2 outline-none"
                            :class="errors.confirmEmail ? 'border-red-500 field-error' : 'border-slate-300 focus:border-indigo-500'"
                            :aria-invalid="!!errors.confirmEmail"
                            @blur="onConfirmEmailBlur"
                          />
                          <p v-if="errors.confirmEmail" class="mt-1 text-xs text-red-600">{{ errors.confirmEmail }}</p>
                        </div>
                        <div class="">
                          <label class="block text-xs text-slate-600 mb-1">Phone number *</label>
                          <VueTelInput
                            v-model="phoneDisplay"
                            :defaultCountry="phoneCountryIso"
                            @validate="onPhoneValidate"
                            @blur="onPhoneBlur"
                            @country-changed="onCountryChanged"
                            :inputOptions="{
                              inputClass: 'w-full rounded border px-3 py-2 outline-none ' + (errors.phone ? 'border-red-500 field-error' : 'border-slate-300 focus:border-indigo-500')
                            }"
                          />
                          <p v-if="errors.phone" class="mt-1 text-xs text-red-600">{{ errors.phone }}</p>
                        </div>  
                        <div v-if="isAuPhone">
                          <label class="block text-xs text-slate-600 mb-1">Postcode *</label>
                          <input
                            v-model="postcode"
                            type="text"
                            inputmode="numeric"
                            maxlength="4"
                            pattern="\d*"
                            class="w-full rounded border px-3 py-2 outline-none"
                            :class="errors.postcode ? 'border-red-500 field-error' : 'border-slate-300 focus:border-indigo-500'"
                            :aria-invalid="!!errors.postcode"
                            @blur="onPostcodeBlur"
                          />
                          <p v-if="errors.postcode" class="mt-1 text-xs text-red-600">{{ errors.postcode }}</p>
                        </div>                                          
                      </div>

                      <div class="mt-4 space-y-2">
                        <label class="flex items-start gap-3 cursor-pointer">
                          <input v-model="mailingList" type="checkbox">
                          <span class="text-sm text-slate-700">Keep me updated on more events and news from this organiser.</span>
                        </label>
                        <label class="flex items-start gap-3 cursor-pointer">
                          <input v-model="acceptedTerms" type="checkbox" @change="onTermsBlur">
                          <span class="text-sm text-slate-700">
                            I agree to the <a href="https://www.summerjam.com.au/content/uploads/2025/11/terms-and-conditions.pdf" target="_blank" class="text-blue-600 hover:underline">Terms &amp; Conditions</a> and <a href="https://www.summerjam.com.au/content/uploads/2025/11/privacy-policy.pdf" target="_blank" class="text-blue-600 hover:underline">Privacy Policy</a>.
                          </span>
                        </label>
                        <p v-if="errors.terms" class="mt-1 -mb-1 text-xs text-red-600 field-error">{{ errors.terms }}</p>
                      </div>
                    </div>

                    <!-- Payment -->
                    <div class="rounded-lg border border-slate-200 p-4">
                      <h3 class="text-base font-semibold mb-3 flex justify-between items-center">
                        {{ isFree ? 'Review & complete' : 'Pay with' }}
                        <p v-if="!isFree" class="text-xs font-normal italic">Tickets are non refundable</p>
                      </h3>

                      <!-- PAID path (Stripe UI) -->
                      <template v-if="!isFree">
                        <div v-if="!clientSecret" class="text-sm text-slate-500 mb-4">
                          Loading secure payment form
                          <span
                            class="inline-block w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"
                            aria-hidden="true"
                          ></span>
                        </div>
                        <div id="payment-element" class="mb-4"></div>

                        <button
                          class="w-full h-11 rounded bg-slate-900 text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          :disabled="paying || !paymentReady"
                          @click="placeOrder()"
                        >
                          <span v-if="paying" class="btn-spinner" aria-hidden="true"></span>
                          <span>{{ paying ? 'Processing…' : 'Place order' }}</span>
                        </button>

                        <p class="text-[11px] text-slate-500 mt-2">
                          Cards, Apple Pay & Google Pay supported.
                          <span v-if="paying" class="inline-block ml-2 text-slate-600">Confirming your order…</span>
                        </p>
                      </template>

                      <!-- FREE path (no Stripe) -->
                      <template v-else>
                        <p class="text-sm text-slate-600 mb-4">
                          No payment required. Confirm your details and complete the order.
                        </p>

                        <button
                          class="w-full h-11 rounded bg-slate-900 text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          :disabled="paying"
                          @click="completeFreeOrder()"
                        >
                          <span v-if="paying" class="btn-spinner" aria-hidden="true"></span>
                          <span>{{ paying ? 'Finalising…' : 'Complete order' }}</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Success (directly after polling success) -->
                <div v-else key="success" data-step="success" class="step-pane">
                  <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-4">
                    <div class="font-bold text-emerald-800 mb-1">Thanks for your order!</div>
                    <div class="text-sm text-emerald-700">
                      <template v-if="orderNumber">Order #{{ orderNumber }}</template>
                      <template v-else-if="orderId">Order ID {{ orderId }}</template>
                    </div>
                    <div class="text-sm text-emerald-700 mt-1">
                      We’ve emailed your tickets and order confirmation to <strong>{{ email }}</strong>.
                    </div>
                  </div>

                  <div class="rounded-lg border border-slate-200 p-4">
                    <div class="flex items-center gap-4">
                      <img v-if="heroUrl" :src="heroUrl" :alt="heroAlt" class="w-20 h-14 object-cover rounded-md hidden sm:block" referrerpolicy="no-referrer" />
                      <div class="min-w-0">
                        <div class="font-medium text-slate-900 truncate">{{ event?.name }}</div>
                        <div class="text-xs text-slate-500">{{ dateLine }}</div>
                      </div>
                      <div class="ml-auto font-semibold">AU${{ cents(safeTotals.total_cents_inc) }}</div>
                    </div>

                    <div class="mt-4 flex flex-col sm:flex-row gap-3">
                      <button
                        v-if="singleGroup"
                        type="button"
                        class="btn-ticketing-red text-white inline-flex items-center justify-center h-10 px-4 rounded font-medium"
                        @click="openTicketsLink"
                      >
                        View tickets
                      </button>

                      <button
                        v-if="!isInIframe()"
                        type="button"
                        class="inline-flex items-center justify-center h-10 px-4 rounded border border-slate-300 text-slate-700"
                        @click="goBackToEvent"
                      >
                        Back to event
                      </button>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>

        </div>

        <!-- RIGHT: summary -->
          <aside
            class="md:col-span-1 ticket-widget__summary bg-white"
            :class="summaryHostClass"
          >
          <div class="sticky top-0">
            <div :class="summaryWrapperClass">
              <div class="hidden md:block ticket-widget__summary__img js-header-height">
                  <img v-if="heroUrl" :src="heroUrl" :alt="heroAlt" class="hidden md:block w-full h-40 object-cover" referrerpolicy="no-referrer" />
              </div>
              
              <div class="ticket-widget--padding flex-grow ticket-widget__summary__order-items">
                <h3 class="text-sm font-semibold mb-4">Order summary</h3>
                  <div
                    v-for="i in displayItems"
                    :key="i.id"
                    class="flex justify-between items-start py-1"
                  >
                    <div class="flex">
                      <span class="qty-col text-sm text-slate-800">{{ i.quantity }}</span>
                      <span class="text-sm text-slate-500">×</span>
                      <div class="pl-2">
                        <div class="text-sm text-slate-800">{{ i.ticket_name }}</div>


                      </div>
                    </div>

                    <!-- ⬇️ new right side -->
                    <div class="text-right">
                      <!-- crossed-out original (per unit), matching the ticket card -->
                      <div
                        v-if="isDiscountedLine(i) && lineStrikeInc(i) !== null"
                        class="text-xs text-slate-400 line-through"
                      >
                        ${{ cents(lineStrikeInc(i) || 0) }}
                      </div>

                      <!-- actual line total (inc), including fee & tax -->
                      <div class="text-sm text-slate-800">
                        ${{ cents(i.line_total_cents_inc) }}
                      </div>
                    </div>
                  </div>

                                
                  <div v-if="session && session.totals" class="md:hidden ticket-widget__summary-mobile">
                    <!-- Tickets (full, before discount) -->
                    <div class="flex justify-between text-sm mb-2 mt-3 border-t border-slate-200 pt-3">
                      <span class="text-slate-600">
                        Subtotal
                      </span>
                      <span>
                        <span>${{ cents(displaySubtotalIncCents) }}</span>
                      </span>
                    </div>

                    <!-- Booking / processing fee (full, before discount) -->
                    <div
                      v-if="bookingFeeIncCents > 0"
                      class="flex justify-between text-sm mb-2"
                    >
                      <span class="text-slate-600">
                        {{ safeTotals.booking_fee_label || 'Booking Fee' }}
                      </span>
                      <span>
                        <span>${{ cents(displayFeeIncCents) }}</span>
                      </span>
                    </div>

                    <!-- Discount (inc) -->
                    <div
                      v-if="(safeTotals.discount_cents_inc ?? 0) > 0"
                      class="flex justify-between text-sm text-emerald-600 mt-1"
                    >
                      <span>Discount</span>
                      <span>-${{ cents(safeTotals.discount_cents_inc ?? 0) }}</span>
                    </div>

                    <!-- TOTAL (inc) -->
                    <div class="flex justify-between font-semibold py-3 border-t border-slate-200 mt-2">
                      <span>
                        TOTAL
                        <span
                          v-if="(taxRatePct || 0) > 0"
                          class="text-xxs align-bottom relative bottom-0.5 text-slate-600 font-normal"
                        >
                          (inc)
                        </span>
                      </span>
                      <span>
                        ${{ cents(safeTotals.total_cents_inc ?? 0) }}
                      </span>
                    </div>
                  </div>                 
              </div> 
                         

              




              <div class="ticket-widget--padding">
               <div v-if="session && session.totals" class="hidden md:block">
                <!-- Tickets (full, before discount) -->
                <!-- Subtotal = tickets + processing (inc) -->
                <div class="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${{ cents(displaySubtotalIncCents) }}</span>
                </div>

                <!-- Booking fee (inc), no processing in here -->
                <div
                  v-if="bookingFeeIncCents > 0"
                  class="flex justify-between text-sm mt-1"
                >
                  <span>{{ safeTotals.booking_fee_label || 'Booking Fee' }}</span>
                  <span>${{ cents(displayFeeIncCents) }}</span>
                </div>

                <!-- Discount -->
                <div
                  v-if="safeTotals.discount_cents_inc"
                  class="flex justify-between text-sm text-emerald-600 mt-1"
                >
                  <span>Discount</span>
                  <span>-${{ cents(safeTotals.discount_cents_inc) }}</span>
                </div>

                <!-- TOTAL (inc) -->
                <div class="flex justify-between mt-2 font-semibold border-t pt-2 pb-2">
                  <span>TOTAL <span class="text-xxs">(inc)</span></span>
                  <span>A${{ cents(safeTotals.total_cents_inc) }}</span>
                </div>
              </div>


      
                  
<!-- CTA -->
<div
  class="mt-0 md:border-t border-slate-200 md:pt-4 cta-wrapper"
  :class="{ 'is-hidden': step !== 'tickets' }"
  :aria-hidden="(step !== 'tickets').toString()"
>
  <!-- Mobile handle: toggles drawer -->
<button
  class="mobile-cart-handle flex md:hidden"
  type="button"
  @click="toggleCart"
  :class="{ 'is-open': isCartOpen }"
  :aria-expanded="isCartOpen ? 'true' : 'false'"
>
  <!-- Left: label + arrow -->
  <div class="flex items-center gap-1">
    <span class="mobile-cart-label text-sm font-medium">
      View order summary
    </span>

    <svg
      class="mobile-cart-caret"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <!-- Up arrow, will rotate down when open -->
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>

  <!-- Right: amount (still clickable because whole button is) -->
  <span class="mobile-cart-amount text-sm font-semibold">
    ${{ cents(safeTotals.total_cents_inc ?? 0) }}
  </span>
</button>

<button
  class="btn-ticketing-red w-full h-11 rounded text-white font-medium disabled:opacity-50 flex items-center justify-center"
  :disabled="totalQty === 0 || isProcessingCheckout"
  @click="goToCheckout()"
>
  <span v-if="!isProcessingCheckout">Checkout</span>

  <!-- SMALL SPINNER -->
  <span v-else class="spinner"></span>
</button>
</div>  
              </div>


              






            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>

</template>


<style lang="scss">

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.back-button .spinner {
  border-color: rgba(99, 102, 241, 0.4); /* indigo-500-ish */
  border-top-color: rgb(99, 102, 241);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.line-clamp{display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden} 
.prose :where(p){margin:0}
.slide-x-enter-active,.slide-x-leave-active{transition:all .25s ease}
.slide-x-enter-from{opacity:0;transform:translateX(12px)}
.slide-x-leave-to{opacity:0;transform:translateX(-12px)}
/* helps scroll-to-error land nicely */
.form-general-error, .field-error { scroll-margin-top: 80px; }

/* === Button spinner === */
@keyframes spin {
  to { transform: rotate(360deg); }
}
.btn-spinner {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 2px solid rgba(255,255,255,0.6);
  border-top-color: #ffffff;
  animation: spin .8s linear infinite;
}

.ticket-groups {
  &__group {
    background-color:transparent;
    border:1px solid var(--color-widget-secondary);
    &--selected {
      background-color:var(--color-widget-primary);
      border:1px solid var(--color-widget-primary);
    }
  }
}

.ticket-group {
    background-color:transparent;
    border:1px solid var(--color-widget-secondary);
    border-radius:3px;
    &:hover, &--selected {
      background-color:var(--color-widget-primary);
      border:1px solid var(--color-widget-primary);
      color:white;
    }

}

.ticket-widget {
  background-color:#F3F3F3;
}

.ticket-card {
  border-width:2px;
  background-color:white;
  color:black;

  &--disabled {
    background-color:#E7E7E7;
    color:#393939;
    opacity: 0.55;
    pointer-events: none; // disables qty buttons etc., but keeps text readable    
  }

  &--ends {
    border-color:var(--color-ticket-badge--ending);
  }

  &--ending {
    border-color:var(--color-ticket-badge--ending);
  }

  &--discounted, &--revealed {
      border-color: var(--color-ticket-badge); 
      .ticket-card__badge {
        background: var(--color-ticket-badge) !important;
      }      
  }

  &--ending {
    border-color: var(--color-ticket-badge--ending);

    .ticket-card__badge {
      background: var(--color-ticket-badge--ending) !important;
    }
  }

  &--soldout {
    border-color: #dc2626;

    .ticket-card__badge {
      background: #dc2626 !important;
    }
  }
  &__badge {
    position: absolute;
    top: -13.5px;
    right: 13.5px;
    background: var(--color-ticket-badge);
    color: #fff;
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    padding: 8px 10px;
    border-radius: 6px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
    height: 27px;
    display: flex;
    align-items: center;  

    @media(max-width:650px) {
      height: 21px;
      padding: 3px 10px;
      font-size: 10.5px;
      top: -10.5px;
    }
  }

  &__header {
    border-bottom:1px solid #DFDFDF;
    &__ticket {
      border-right:1px solid #DFDFDF;
      width:50%;
      &__label {
        font-size:14px;
      }
      &__item {
        font-size:16px;
        font-weight:600;
      } 
    }
    

    &__price {
      width:25%;
    }
    &__qty {
      width:25%;
      justify-self:flex-end;
      position:relative;
      bottom:4px;

      &__btn {
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        width:24px;
        height:24px;
        font-size:23px;

        &:disabled {
          border:1px solid rgb(78, 78, 78);
        }
        &--decrease {
          border:1px solid black;
          span {
            position:relative;
            bottom:2px;
          }
        }
      }
    }
   
  }
}


@media(max-width:767px) {
  .ticket-content-area {
    overflow-y: auto;
    height: 100svh;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
  @media(max-width:767px) {

    .tickets-step-1 {
      padding-bottom:104px;
    }
  }
}



.ticket-widget__summary {

  @media(min-width:768px) {
    height:100%;

    // .sticky, .ticket-widget__summary--wrapper {
    //   height:100%;
    // }
  }
  &--wrapper {
    display:flex;
    flex-direction:column;
    transition:0.3s;
    
    @media(max-width:767px) {
      height:100vh;
      max-height:114px !important;

      &.is-expanded {
        /* expanded: let content reveal upwards (still bounded by viewport) */
        max-height: 100vh !important;
      }
    }
    
  }

  &__img {
    
    img {
      height: -moz-fit-content;
      height: fit-content;
      height: 100%;
      -o-object-fit: cover;
      object-fit: cover;
      width: 100%;    
    }

  }




  @media(max-width:767px) {
    position: fixed;
    bottom: 0vh;
    width: 100%;
    z-index: 9999;

   
    

    .ticket-widget__summary--wrapper {
      padding: 15px;
      height: 100vh;
      // border-top-left-radius: 100px;
      // border-top-right-radius: 100px;
    }

    .ticket-widget--padding {
      padding:0px !important;
    }

  }

  &__order-items {
    
    overflow:scroll;
    overflow-y: scroll;     /* scrolling still works */
    scrollbar-width: none;  /* Firefox */
    -ms-overflow-style: none; /* IE and old Edge */  
    &::-webkit-scrollbar {
      display:none;
    }
  }   
}

/* Mobile handle (bar above Checkout) */
/* Mobile handle (bar above Checkout) */
.mobile-cart-handle {
  width: 100%;
  align-items: center;
  justify-content: space-between; // ⬅️ was flex-end
  gap: 10px;
  background: #fff;
  margin-bottom: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  .mobile-cart-caret {
    width: 16px;
    height: 16px;
    transition: transform .25s ease;
    transform: rotate(180deg); // default: pointing up
  }
}

/* When open, rotate arrow down */
.mobile-cart-handle.is-open .mobile-cart-caret {
  transform: rotate(180deg);
}

/* Rotate caret when open */
.is-expanded .mobile-cart-caret {
  transform: rotate(0deg);
}

.mobile-cart-total {
  font-size: 13px;
  color: #6b7280;
  margin-left: auto;
  margin-right: 6px;
}


.ticket-widget--padding {
  padding:15px;
  @media (min-width:767px) {
    padding:24px;  
  }    
}

.ticket-widget__header {
  background-color:var(--color-widget-secondary);
  color:white;
  position:sticky;
  top:0px;
  z-index:2222;

}

.js-header-height {

}

.btn-ticketing-red {
  background-color:var(--color-widget-primary);
  transition:0.3s;
  &:hover {
    background-color:var(--color-widget-secondary);
  }
}


.promo-code-button {
  &--wrapper {
    position:relative;
    width:100%;
  }

  &__input {
    font-size:13px !important;
    &::placeholder {
      font-size:13px !important;
    }
    
    transition:0.3s;
    box-shadow:none !important;
    &:focus {
      border:1px solid black;
    }
  }
  &__button {
    position:absolute;
    right:6px;
    top:7px;
    height:70%;
    padding-top:0px;
    padding-bottom:0px;
  }
}

// @media(max-width:767px) {
//   .ticket-widget__header__logo {
//     display:none
//   }
// }

@media(max-height:800px) {
  .ticket-widget__header__logo {
    display:none
  }
}

.ticket-card__badge--soldout {
  background-color: #dc2626 !important;
  color: #fff;
}

// .ticket-card__badge--soldout {
//   background-color: #999;
//   color: #fff;
//   opacity: 0.8;
// }

.ticket-card__badge--limit {
  background-color: #000;
  color: #fff;
}

.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }


/* Collapsible CTA area (desktop + mobile) */
.cta-wrapper {
  overflow: hidden;
  transition:
    max-height .28s ease,
    opacity .18s ease,
    transform .28s ease,
    padding-top .2s ease,
    margin-top .2s ease;
  max-height: 220px;     /* big enough for handle + button */
  opacity: 1;
  transform: translateY(0);

  &.is-hidden {
    max-height: 0 !important;
    opacity: 0;
    transform: translateY(8px);
    padding-top: 0 !important;
    margin-top: 0 !important;
    pointer-events: none;
  }
}

/* Smooth slide for the entire right summary on mobile when not on 'tickets' */
@media (max-width: 767px) {
  .ticket-widget__summary--wrapper {
    transition:
      max-height .28s ease,
      transform .28s ease,
      opacity .2s ease;
  }

  /* When not on tickets step, hide the whole drawer */
  // .ticket-widget__summary--wrapper.is-collapsed-mobile {
  //   max-height: 0 !important;
  //   opacity: 0;
  //   transform: translateY(8px);
  //   pointer-events: none;
  // }

  /* Keep your existing default/expanded heights */
  .ticket-widget__summary--wrapper:not(.is-collapsed-mobile) {
    max-height: 104px !important; // your default closed height
  }
  .ticket-widget__summary--wrapper.is-expanded:not(.is-collapsed-mobile) {
    max-height: 100vh !important; // your expanded height
  }
}

.back-button {
  border: 1px solid #a4a4a4;
  border-radius: 20px;
  padding: 1px 10px;
  color: black;  
  transition:0.3s;
  height:fit-content;
  &:hover {
    text-decoration: none;
    background-color:black;
    color:white;
  }
}

/* Smooth host collapse on mobile */
@media (max-width: 767px) {
  .ticket-widget__summary {
    overflow: hidden;
    transition: max-height .3s ease;
    max-height: 104px;

    &.is-collapsed { max-height: 0 !important; pointer-events: none; }
    &.is-expanded  { max-height: 100vh !important; }

    &.no-transition { transition: none !important; } // ⬅️ new
  }
}



.ticket-widget {
  @media(max-width:767px) {
    min-height: 100vh;
    min-height: 100svh;
    min-height: 100dvh;
  }

  input {
    font-size:14px !important;
  }
}


@media (max-width: 767px) {
  /* When the browser supports dynamic viewport units */
  @supports (height: 100dvh) {
    .js-ticket-right-wrapper {
      /* header height injected as a CSS var from JS */
      height: calc(100dvh - var(--headerH, 0px)) !important;
    }
  }

  /* Optional: keep a baseline so content doesn’t flash before JS runs */
  .js-ticket-right-wrapper {
    /* a safe starting point; JS will set exact --headerH soon after mount */
    height: 100vh;
  }
}

  .ticket-card__header__price.mobile-only {
    display:none;
  }
@media(max-width:650px) {


  .ticket-card__header__qty {
    width:120px;
    .gap-3 {
        gap:6px;
      }
  }
  .ticket-card__header__ticket {
    width:100%;
    flex-grow:1;
    padding-right:15px;
  }
  .ticket-card__header__price.mobile-only {
    display:block;
    width:100%;
    .ticket-card__header__ticket__item {
      font-size:13px;
    }
  }
  .ticket-card__header__price.desktop-only {
    display:none;
  }
  .ticket-card__header__ticket__label {
    font-size:13px;
    margin-bottom:3px;
  }
  .ticket-card__header__ticket__item {
    font-size:14px;
    font-weight:500;
  }
}
.ticket-group {
  @media(max-width:370px) {
    font-size: 12px;
    padding: 2px 11px;
  }
  @media(max-width:485px) {
    font-size: 13px;
    padding: 3px 12px;
  }  
}

.ticket-card__description {
  p, h1, h2, h3, h4, ol, ul {
    padding-bottom:5px;
  }
  ul, ol {
    list-style: initial;
    margin: initial;
    padding-left: 24px;    
  }
}

</style>